import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import FoodMacros from './FoodMacros';
import './Journal.css';
import Modal from '../common/Modal';
import FoodImageModal, { getFoodImageGallery } from '../common/FoodImageModal';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchFoodForm, { invalidateFavoriteFoodsCache } from './SearchFoodForm';
import MealItemEditor from './MealItemEditor';
import AddFoodForm from './AddFoodForm';
import EditFoodForm from './EditFoodForm';
import FoodNoteForm from './FoodNoteForm';
import journalService from '../../services/journalService';
import openFoodFactsService from '../../services/openFoodFactsService';
import friendsService from '../../services/friendsService';
import favoriteFoodsService from '../../services/favoriteFoodsService';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faStickyNote, faChevronDown, faGripVertical, faCopy, faPen, faCheck, faTrash, faMagnifyingGlass, faPlus, faFilePdf, faFileExcel, faCalendarDays, faClock, faArrowUp, faArrowDown, faStar } from '@fortawesome/free-solid-svg-icons';
import settingsJournalService from '../../services/settingsJournalService';
import { hasPremiumAccess } from '../../utils/subscription';
import { buildAdjustedNutritionForDisplay } from '../../utils/nutritionData';
import { rememberFoodNote } from '../../utils/foodNoteMemory';
import { buildFavoriteFoodPayload, matchesFavoriteFood } from './favoriteFoodUtils';
import {
  AVAILABLE_MACROS,
  DEFAULT_FOOD_MAIN_MACROS,
} from '../../config/nutritionMacros';
import * as XLSX from 'xlsx';
import { SharedDatePicker } from './SharedDatePicker.styled';

const SITE_NAME = process.env.REACT_APP_SITE_NAME;
const getCurrentLocalTimeValue = () => new Date().toTimeString().slice(0, 5);

const unwrapOpenFoodFactsProduct = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  if (value.product && typeof value.product === 'object' && !Array.isArray(value.product)) {
    return value.product;
  }

  if (
    value.data?.product
    && typeof value.data.product === 'object'
    && !Array.isArray(value.data.product)
  ) {
    return value.data.product;
  }

  return value;
};

const parseFoodSourceData = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return unwrapOpenFoodFactsProduct(value);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return unwrapOpenFoodFactsProduct(parsed);
    } catch {
      return null;
    }
  }

  return null;
};

const getFoodBarcode = (food, sourceData = null) => String(
  food?.barcode
  || food?.code
  || sourceData?.code
  || sourceData?._id
  || sourceData?.id
  || ''
).trim();

const FOOD_IMAGE_FIELDS = [
  'image_front_url',
  'image_front_small_url',
  'image_front_thumb_url',
  'image_ingredients_url',
  'image_ingredients_small_url',
  'image_nutrition_url',
  'image_nutrition_small_url',
  'image_packaging_url',
  'image_packaging_small_url',
  'image_url',
  'image_thumb_url',
  'image_small_url',
];

const buildFoodImagePreview = ({ food, sources, imageUrl, displayName, barcode }) => {
  const imageSources = sources.filter(Boolean);
  const preview = {
    src: String(imageUrl || '').trim(),
    name: displayName || 'Aliment',
    brand: '',
    code: barcode,
  };

  FOOD_IMAGE_FIELDS.forEach((field) => {
    preview[field] = imageSources
      .map((source) => String(source?.[field] || '').trim())
      .find(Boolean) || '';
  });

  preview.brand = imageSources
    .map((source) => String(source?.brands || source?.brand || '').trim())
    .find(Boolean)
    || String(food?.brand || food?.brands || '').trim();

  return preview;
};

const SummaryWrapper = styled.div`
    background: linear-gradient(135deg, #ffffff, #f8fafb);
    border-radius: 16px;
    padding: 1.5rem;
    margin-top: 1.5rem;
    box-shadow: 0 4px 12px rgba(26, 46, 64, 0.08);
    border: 1px solid #e1e8ed;

    @media (max-width: 576px) {
      padding: 1rem;
      margin-top: 1.25rem;
    }
  `;

const SummaryTitle = styled.h3`
    font-family: 'Poppins', 'Roboto', sans-serif;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1A2E40;
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `;

const FoodNote = styled.div`
  background: #fafafa;
  border-radius: clamp(8px, 2vw, 10px);
  padding: clamp(0.7rem, 2.5vw, 1.1rem) clamp(0.85rem, 2.8vw, 1.1rem);
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);
  color: #333;
  line-height: 1.55;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  margin: clamp(0.5rem, 1.5vw, 0.75rem) 0 clamp(0.75rem, 2vw, 1rem) 0;
  white-space: pre-wrap;
  word-wrap: break-word;

  @media (max-width: 576px) {
    border-radius: 8px;
    padding: 0.7rem 0.85rem;
    margin: 0.5rem 0 0.75rem 0;
  }

  @media (max-width: 400px) {
    padding: 0.6rem 0.75rem;
    margin: 0.4rem 0 0.6rem 0;
  }
`;

const NoteIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(0.35rem, 1.5vw, 0.5rem);
  color: #777;
  font-weight: 600;
  margin-bottom: clamp(0.3rem, 1vw, 0.4rem);
  font-size: clamp(0.85rem, 2.3vw, 0.95rem);
  
  svg {
    width: clamp(13px, 3.5vw, 15px);
    height: clamp(13px, 3.5vw, 15px);
    flex-shrink: 0;
  }

  @media (max-width: 576px) {
    gap: 0.35rem;
    margin-bottom: 0.3rem;
    font-size: 0.85rem;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  @media (max-width: 400px) {
    gap: 0.3rem;
    margin-bottom: 0.25rem;
    font-size: 0.8rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const NoteText = styled.div`
  font-style: normal;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #444;
  font-size: clamp(0.8rem, 2.1vw, 0.9rem);
  line-height: 1.6;
  overflow-wrap: break-word;

  @media (max-width: 576px) {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  @media (max-width: 400px) {
    font-size: 0.75rem;
    line-height: 1.45;
  }
`;

const SUMMARY_GOAL_CONFIG = Object.freeze({
  'energy-kcal': { goalKey: 'targetCalories', color: '#4CAF50' },
  'proteins': { goalKey: 'targetProtein', color: '#E53935' },
  'carbohydrates': { goalKey: 'targetCarbs', color: '#FFEB3B' },
  'fat': { goalKey: 'targetFats', color: '#1A2E40' },
  'fiber': { goalKey: 'targetFiber', color: '#8BC34A' },
  'sugars': { goalKey: 'targetSugars', color: '#FF5722' },
  'added-sugars': { goalKey: 'targetAddedSugars', color: '#FF5722' },
  'starch': { goalKey: 'targetStarch', color: '#FFC107' },
  'saturated-fat': { goalKey: 'targetSaturatedFat', color: '#FF5722' },
  'trans-fat': { goalKey: 'targetTransFat', color: '#D32F2F' },
  'monounsaturated-fat': { goalKey: 'targetMonounsaturatedFat', color: '#4CAF50' },
  'polyunsaturated-fat': { goalKey: 'targetPolyunsaturatedFat', color: '#4CAF50' },
  'cholesterol': { goalKey: 'targetCholesterol', color: '#E91E63' },
  'sodium': { goalKey: 'targetSodium', color: '#FF9800' },
  'salt': { goalKey: 'targetSalt', color: '#FF9800' },
  'vitamin-a': { goalKey: 'targetVitaminA', color: '#FF9800' },
  'vitamin-b1': { goalKey: 'targetVitaminB1', color: '#CDDC39' },
  'vitamin-b2': { goalKey: 'targetVitaminB2', color: '#CDDC39' },
  'vitamin-b6': { goalKey: 'targetVitaminB6', color: '#CDDC39' },
  'vitamin-b9': { goalKey: 'targetVitaminB9', color: '#CDDC39' },
  'vitamin-b12': { goalKey: 'targetVitaminB12', color: '#CDDC39' },
  'vitamin-c': { goalKey: 'targetVitaminC', color: '#FFEB3B' },
  'vitamin-d': { goalKey: 'targetVitaminD', color: '#FF9800' },
  'vitamin-e': { goalKey: 'targetVitaminE', color: '#4CAF50' },
  'vitamin-k': { goalKey: 'targetVitaminK', color: '#4CAF50' },
  'calcium': { goalKey: 'targetCalcium', color: '#0097A7' },
  'iron': { goalKey: 'targetIron', color: '#0288D1' },
  'magnesium': { goalKey: 'targetMagnesium', color: '#9C27B0' },
  'zinc': { goalKey: 'targetZinc', color: '#607D8B' },
  'copper': { goalKey: 'targetCopper', color: '#795548' },
  'manganese': { goalKey: 'targetManganese', color: '#9E9E9E' },
  'phosphorus': { goalKey: 'targetPhosphorus', color: '#FF5722' },
  'potassium': { goalKey: 'targetPotassium', color: '#3F51B5' },
  'selenium': { goalKey: 'targetSelenium', color: '#607D8B' },
  'choline': { goalKey: 'targetCholine', color: '#9C27B0' },
  'caffeine': { goalKey: 'targetCaffeine', color: '#795548' },
  'alcohol': { goalKey: 'targetAlcohol', color: '#E91E63' },
});

const DEFAULT_GOAL_SUMMARY_MACROS = new Set(['energy-kcal', 'proteins', 'carbohydrates', 'fat']);
const SUMMARY_STANDARD_MACROS = Object.freeze(['proteins', 'carbohydrates', 'fat']);
const ADVANCED_GOAL_SUMMARY_MACROS = new Set(['fiber', 'sugars', 'sodium', 'saturated-fat']);
const SUMMARY_FOCUSED_MACROS = new Set([
  ...SUMMARY_STANDARD_MACROS,
  'fiber',
  'sugars',
  'sodium',
  'saturated-fat',
]);

const isCoarsePointerViewport = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  return false;
};

const SUMMARY_MACRO_MAPPING = Object.freeze(
  AVAILABLE_MACROS.reduce((mapping, macro) => {
    const goalConfig = SUMMARY_GOAL_CONFIG[macro.key] || {};
    mapping[macro.key] = {
      dataKey: macro.key,
      label: macro.label,
      unit: macro.unit,
      goalKey: goalConfig.goalKey || null,
      color: goalConfig.color || '#64748b'
    };
    return mapping;
  }, {})
);

const Journal = ({ friendUsername = null, demoData = null } = {}) => {
  const { user } = useAuth();
  const isPremium = hasPremiumAccess(user);
  const isFriendView = typeof friendUsername === 'string' && friendUsername.trim().length > 0;
  const isReadOnly = isFriendView;
  const mealTableColSpan = isReadOnly ? 7 : 8;
  const [journalId, setJournalId] = useState(null);
  const [allMeals, setAllMeals] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [draggedFood, setDraggedFood] = useState(null);
  const [foodOverTarget, setFoodOverTarget] = useState(null);
  const [selectedFoodsByMeal, setSelectedFoodsByMeal] = useState({});
  const [bulkMoveTargetByMeal, setBulkMoveTargetByMeal] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [showToastMsg, setShowToastMsg] = useState(null);
  const [favoriteFoods, setFavoriteFoods] = useState([]);
  const [summaryMacros, setSummaryMacros] = useState([]);
  const [summaryChartHoverKey, setSummaryChartHoverKey] = useState(null);
  const [summaryChartTooltip, setSummaryChartTooltip] = useState(null);
  const [hideUnavailableFoodMacros, setHideUnavailableFoodMacros] = useState(false);
  const [displayedMacros, setDisplayedMacros] = useState([]);
  const [mainMacros, setMainMacros] = useState([]);
  const [nutritionalGoals, setNutritionalGoals] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCopyDrawerOpen, setIsCopyDrawerOpen] = useState(false);
  const [copyMode, setCopyMode] = useState('day_copy');
  const [copySourceDate, setCopySourceDate] = useState('');
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [copySourceMealId, setCopySourceMealId] = useState('');
  const [copyDestinationMealId, setCopyDestinationMealId] = useState('');
  const [editingMealId, setEditingMealId] = useState(null);
  const [mealNameDrafts, setMealNameDrafts] = useState({});
  const [editingMealTimeId, setEditingMealTimeId] = useState(null);
  const [mealTimeDrafts, setMealTimeDrafts] = useState({});
  const [savingMealTimeId, setSavingMealTimeId] = useState(null);
  const [invalidatedBarcode, setInvalidatedBarcode] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 1024;
  });
  const [isCoarsePointerMode, setIsCoarsePointerMode] = useState(() => isCoarsePointerViewport());
  const [foodImageCache, setFoodImageCache] = useState({});
  const [foodImagePreview, setFoodImagePreview] = useState(null);
  const PRODUCT_PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 fill=%22%23eef2f7%22/%3E%3Ctext x=%2240%22 y=%2245%22 text-anchor=%22middle%22 font-family=%22Arial,sans-serif%22 font-size=%2210%22 fill=%22%2362748a%22%3EProduit%3C/text%3E%3C/svg%3E';
  const [collapsedFoodIngredients, setCollapsedFoodIngredients] = useState({});
  const [lastActiveMealIndex, setLastActiveMealIndex] = useState(0);
  const [openMealActionsId, setOpenMealActionsId] = useState(null);
  const [isMobileHeaderActionsOpen, setIsMobileHeaderActionsOpen] = useState(false);

  const [searchState, setSearchState] = useState({
    visible: false,
    mealIndex: null,
    query: '',
    loading: false,
    results: [],
    selectedIndex: 0
  });

  const [loading, setLoading] = useState(false);
  const [isFoodMutationLoading, setIsFoodMutationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const foodPreviewMacros = DEFAULT_FOOD_MAIN_MACROS;
  const allDetailMacroKeys = useMemo(
    () => AVAILABLE_MACROS.map((macro) => macro.key),
    []
  );

  const debounceTimer = useRef(null);
  const mealNameInputRefs = useRef({});
  const isSyncingQueuedMealsRef = useRef(false);
  const foodImageFetchRef = useRef(new Map());
  const foodImageSearchRef = useRef(new Map());
  const foodMutationInFlightRef = useRef(false);
  const touchDragPreviewRef = useRef(null);
  const touchDragPreviewOffsetRef = useRef({ x: 16, y: 14 });
  const touchDragGhostFrameRef = useRef(null);
  const touchDragGhostLatestRef = useRef({ x: 0, y: 0 });
  const touchDragStateRef = useRef({
    active: false,
    type: null,
    pointerId: null,
    sourceMealIndex: null,
    targetMealIndex: null,
    sourceMealId: null,
    sourceFoodIndex: null,
    targetFoodIndex: null,
  });

  const openFoodImagePreview = useCallback(({
    food,
    sourceData,
    cachedProduct,
    cachedNameProduct,
    imageUrl,
    displayName,
    barcode,
  }) => {
    const normalizedBarcode = String(
      barcode
      || getFoodBarcode(food, sourceData)
      || cachedProduct?.code
      || cachedProduct?._id
      || cachedNameProduct?.code
      || cachedNameProduct?._id
    ).trim();
    const normalizedName = String(displayName || food?.name || '').trim().toLowerCase();
    const nameCacheKey = normalizedName ? `name:${normalizedName}` : '';
    const buildPreview = (fullProduct = null) => buildFoodImagePreview({
      food,
      sources: [fullProduct, cachedProduct, cachedNameProduct, sourceData, food],
      imageUrl,
      displayName,
      barcode: normalizedBarcode,
    });

    setFoodImagePreview(buildPreview());

    const cachedHasFullImages = Boolean(
      cachedProduct?.image_ingredients_url ||
      cachedProduct?.image_nutrition_url ||
      cachedProduct?.image_packaging_url
    );

    if (cachedHasFullImages) {
      return;
    }

    const applyProductToPreview = (product) => {
      if (!product) {
        return;
      }

      setFoodImageCache((previous) => ({
        ...previous,
        ...(normalizedBarcode ? { [normalizedBarcode]: product } : {}),
        ...(nameCacheKey ? { [nameCacheKey]: product } : {}),
      }));
      setFoodImagePreview((current) => (
        current?.code === normalizedBarcode && current?.name === (displayName || 'Aliment')
          ? buildPreview(product)
          : current
      ));
    };

    if (!normalizedBarcode) {
      if (!normalizedName) {
        return;
      }

      openFoodFactsService
        .searchFoods({
          query: normalizedName,
          page: 1,
          pageSize: 1,
          filters: { sortBy: 'unique_scans_n' },
          allowFuzzy: false,
        })
        .then((data) => applyProductToPreview((data?.products || [])[0] || null))
        .catch(() => null);
      return;
    }

    const existingRequest = foodImageFetchRef.current.get(normalizedBarcode);
    const request = existingRequest || openFoodFactsService.getProductByCode({ code: normalizedBarcode });

    if (!existingRequest) {
      foodImageFetchRef.current.set(normalizedBarcode, request);
    }

    request
      .then((product) => {
        applyProductToPreview(product);
      })
      .catch(() => null)
      .finally(() => {
        if (!existingRequest) {
          foodImageFetchRef.current.delete(normalizedBarcode);
        }
      });
  }, []);

  const scheduleTouchGhostUpdate = useCallback((x, y) => {
    touchDragGhostLatestRef.current = { x, y };

    if (touchDragGhostFrameRef.current) {
      return;
    }

    touchDragGhostFrameRef.current = window.requestAnimationFrame(() => {
      touchDragGhostFrameRef.current = null;
      const next = touchDragGhostLatestRef.current;
      const preview = touchDragPreviewRef.current;
      if (!preview) {
        return;
      }

      const offset = touchDragPreviewOffsetRef.current || { x: 16, y: 14 };
      const left = Math.round(next.x - offset.x);
      const top = Math.round(next.y - offset.y);
      preview.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    });
  }, []);

  const removeTouchDragPreview = useCallback(() => {
    if (touchDragPreviewRef.current?.parentNode) {
      touchDragPreviewRef.current.parentNode.removeChild(touchDragPreviewRef.current);
    }
    touchDragPreviewRef.current = null;
  }, []);

  const createTouchDragPreviewFromElement = useCallback((sourceElement, pointerX, pointerY) => {
    if (typeof document === 'undefined' || !sourceElement) {
      return;
    }

    removeTouchDragPreview();

    const rect = sourceElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'touch-drag-preview';
    const previewContent = document.createElement('div');
    previewContent.className = 'journal-container touch-drag-preview-content';
    wrapper.appendChild(previewContent);

    const isTableRow = String(sourceElement.tagName || '').toUpperCase() === 'TR';
    if (isTableRow) {
      const table = document.createElement('table');
      table.className = 'meal-table touch-drag-preview-table';
      const tbody = document.createElement('tbody');
      const rowClone = sourceElement.cloneNode(true);
      rowClone.classList?.remove('dragging', 'over', 'selected');
      tbody.appendChild(rowClone);
      table.appendChild(tbody);
      previewContent.appendChild(table);
    } else {
      const clone = sourceElement.cloneNode(true);
      clone.classList?.remove('dragging', 'over');
      previewContent.appendChild(clone);
    }

    const maxWidth = Math.max(220, window.innerWidth - 18);
    const previewWidth = Math.min(Math.max(Math.round(rect.width), 220), maxWidth);
    wrapper.style.width = `${previewWidth}px`;

    touchDragPreviewOffsetRef.current = {
      x: Math.min(Math.max(pointerX - rect.left, 14), previewWidth - 14),
      y: Math.min(Math.max(pointerY - rect.top, 12), Math.max(16, rect.height - 8)),
    };

    document.body.appendChild(wrapper);
    touchDragPreviewRef.current = wrapper;
    scheduleTouchGhostUpdate(pointerX, pointerY);
  }, [removeTouchDragPreview, scheduleTouchGhostUpdate]);

  useEffect(() => {
    return () => {
      if (touchDragGhostFrameRef.current) {
        window.cancelAnimationFrame(touchDragGhostFrameRef.current);
        touchDragGhostFrameRef.current = null;
      }
      removeTouchDragPreview();
      if (typeof document !== 'undefined') {
        document.body.classList.remove('touch-drag-active');
      }
    };
  }, [removeTouchDragPreview]);

  const processedImageKeysRef = useRef(new Set());

  useEffect(() => {
    if (demoData) {
      return;
    }

    if (!filteredMeals || filteredMeals.length === 0) {
      return;
    }

    const codesToFetch = new Set();
    const namesToFetch = new Set();

    filteredMeals.forEach((meal) => {
      (meal.foods || []).forEach((food) => {
        const source = parseFoodSourceData(
          food?.sourceData ||
          food?.source_data ||
          food?.openFoodFactsData ||
          null
        );
        const barcode = getFoodBarcode(food, source);

        const hasImage = Boolean(
          source?.image_front_small_url ||
          source?.image_thumb_url ||
          source?.image_front_url ||
          source?.image_url ||
          food?.imageUrl ||
          food?.image_url ||
          food?.image
        );

        if (!hasImage && barcode && !processedImageKeysRef.current.has(barcode)) {
          codesToFetch.add(barcode);
        }

        const nameKey = String(food?.name || "").trim().toLowerCase();
        const nameCacheKey = `name:${nameKey}`;

        if (
          !hasImage &&
          !barcode &&
          nameKey.length >= 3 &&
          !processedImageKeysRef.current.has(nameCacheKey)
        ) {
          namesToFetch.add(nameKey);
        }
      });
    });

    // Recherche par code-barres
    const batch = Array.from(codesToFetch).slice(0, 10);

    batch.forEach((code) => {
      if (foodImageFetchRef.current.has(code)) return;

      processedImageKeysRef.current.add(code);

      const request = openFoodFactsService
        .getProductByCode({ code })
        .then((product) => {
          if (product) {
            setFoodImageCache((prev) => ({
              ...prev,
              [code]: product, // on stocke le produit complet
            }));
          }
        })
        .catch(() => null)
        .finally(() => {
          foodImageFetchRef.current.delete(code);
        });

      foodImageFetchRef.current.set(code, request);
    });

    // Recherche par nom
    const nameBatch = Array.from(namesToFetch).slice(0, 6);

    nameBatch.forEach((nameKey) => {
      if (foodImageSearchRef.current.has(nameKey)) return;

      const nameCacheKey = `name:${nameKey}`;

      processedImageKeysRef.current.add(nameCacheKey);

      const request = openFoodFactsService
        .searchFoods({
          query: nameKey,
          page: 1,
          pageSize: 1,
          filters: { sortBy: "unique_scans_n" },
          allowFuzzy: false,
        })
        .then((data) => {
          const product = (data?.products || [])[0] || null;

          if (product) {
            setFoodImageCache((prev) => ({
              ...prev,
              [nameCacheKey]: product, // on stocke le produit complet
            }));
          }
        })
        .catch(() => null)
        .finally(() => {
          foodImageSearchRef.current.delete(nameKey);
        });

      foodImageSearchRef.current.set(nameKey, request);
    });
  }, [filteredMeals, demoData]);

  useEffect(() => {
    const updateViewportMode = () => {
      const isMobile = window.innerWidth <= 1024;
      setIsMobileViewport(isMobile);
      setIsCoarsePointerMode(isCoarsePointerViewport());
      if (!isMobile) {
        setIsMobileHeaderActionsOpen(false);
      }
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(filteredMeals) || filteredMeals.length === 0) {
      setLastActiveMealIndex(0);
      return;
    }
    if (lastActiveMealIndex >= filteredMeals.length) {
      setLastActiveMealIndex(0);
    }
  }, [filteredMeals, lastActiveMealIndex]);

  const isPendingOfflineMeal = useCallback(
    (mealOrId) => {
      if (!mealOrId) {
        return false;
      }

      if (typeof mealOrId === 'string') {
        return journalService.isOfflineMealId(mealOrId);
      }

      return Boolean(mealOrId.isPendingSync) || journalService.isOfflineMealId(mealOrId.id);
    },
    []
  );

  const toDateKey = useCallback((value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const fromDateKey = useCallback((dateKey) => {
    if (!dateKey || typeof dateKey !== 'string') {
      return new Date();
    }
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) {
      return new Date();
    }
    return new Date(year, month - 1, day);
  }, []);

  const formatDateShort = useCallback((value) => {
    const date = value instanceof Date ? value : fromDateKey(value);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [fromDateKey]);

  const getDateKey = useCallback(() => {
    return toDateKey(currentDate);
  }, [currentDate, toDateKey]);

  const getMealsForDate = useCallback((dateValue) => {
    return journalService.filterMealsByDate(allMeals, dateValue);
  }, [allMeals]);

  const getSourceMealsForDate = useCallback((dateValue) => {
    return getMealsForDate(dateValue).filter(meal => (meal.foods || []).length > 0);
  }, [getMealsForDate]);

  const showToast = useCallback((message) => {
    setShowToastMsg(message);
    setTimeout(() => setShowToastMsg(null), 2600);
  }, []);

  const addFoodToFavorites = useCallback(async (food, displayName, sourceData) => {
    if (!food || isReadOnly) {
      return;
    }

    const payload = buildFavoriteFoodPayload({ food, displayName, sourceData });
    const existingFavorite = favoriteFoods.find((favorite) => matchesFavoriteFood(favorite, payload));

    try {
      if (existingFavorite) {
        await favoriteFoodsService.deleteFavorite(existingFavorite.id);
        setFavoriteFoods((prev) => prev.filter((favorite) => favorite.id !== existingFavorite.id));
        showToast(`${payload.name} retiré des favoris`);
        try {
          try { invalidateFavoriteFoodsCache(); } catch (err) { }
          window.dispatchEvent(new CustomEvent('favoriteFoods:changed', { detail: { action: 'removed', favorite: existingFavorite } }));
        } catch (e) { /* ignore in non-browser env */ }
        return;
      }

      const savedFavorite = await favoriteFoodsService.saveFavorite(payload);
      setFavoriteFoods((prev) => [
        ...prev,
        {
          id: savedFavorite?.id ?? savedFavorite?.recordId ?? `${payload.name}-${payload.code}`,
          name: savedFavorite?.name || payload.name,
          brand: savedFavorite?.brand || payload.brand,
          code: savedFavorite?.code || payload.code,
        },
      ]);
      showToast(`${payload.name} ajouté aux favoris`);
      try {
        try { invalidateFavoriteFoodsCache(); } catch (err) { }
        window.dispatchEvent(new CustomEvent('favoriteFoods:changed', { detail: { action: 'added', favorite: savedFavorite } }));
      } catch (e) { /* ignore in non-browser env */ }
    } catch (error) {
      console.error('Erreur gestion favori depuis le journal:', error);
      showToast(error?.message || 'Impossible de mettre à jour cet aliment dans les favoris pour le moment.');
    }
  }, [favoriteFoods, isReadOnly, showToast]);

  const loadJournalFromAPI = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (demoData) {
        const journalData = demoData.journal || demoData;
        const goals = demoData.goals || {};
        setJournalId(journalData?.id || 'demo-journal');
        setAllMeals(Array.isArray(journalData?.meals) ? journalData.meals : []);
        setNutritionalGoals(goals);
        setSummaryMacros(Array.isArray(goals.summaryMacros) ? goals.summaryMacros : [...SUMMARY_STANDARD_MACROS]);
        setDisplayedMacros(Array.isArray(goals.displayedMacros) ? goals.displayedMacros : []);
        setMainMacros(Array.isArray(goals.mainMacros) ? goals.mainMacros : [...SUMMARY_STANDARD_MACROS]);
        setHideUnavailableFoodMacros(Boolean(goals.hideUnavailableFoodMacros));
        setIsInitialized(true);
        return;
      }

      if (isFriendView) {
        const journalData = await friendsService.getFriendJournalByUsername(friendUsername.trim());
        setJournalId(journalData?.id || null);
        setAllMeals(Array.isArray(journalData?.meals) ? journalData.meals : []);

        // Charger les paramètres du journal de l'utilisateur pour l'affichage
        try {
          const userJournal = await journalService.loadUserJournal();
          const userGoals = await settingsJournalService.getNutritionalGoals(userJournal.id);
          setNutritionalGoals(userGoals);

          const advancedGoalsEnabled =
            isPremium
            && (
              userGoals.advancedMacroGoalsEnabled === true
              || userGoals.advancedMacroGoalsEnabled === 'true'
            );
          const savedSummaryMacros = Array.isArray(userGoals.summaryMacros) ? userGoals.summaryMacros : [];
          const sanitizedSummaryMacros = Array.from(
            new Set(
              savedSummaryMacros
                .filter((macroKey) => SUMMARY_FOCUSED_MACROS.has(macroKey))
                .filter((macroKey) => advancedGoalsEnabled || !ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey))
            )
          );
          setSummaryMacros(sanitizedSummaryMacros.length > 0 ? sanitizedSummaryMacros : [...SUMMARY_STANDARD_MACROS]);
          setHideUnavailableFoodMacros(Boolean(userGoals.hideUnavailableFoodMacros));

          // Appliquer les paramètres d'affichage des aliments
          setDisplayedMacros(userGoals.displayedMacros || []);
          setMainMacros(userGoals.mainMacros || []);
        } catch (err) {
          console.warn('Impossible de charger les paramètres utilisateur pour le journal d\'ami:', err);
          // Utiliser les paramètres par défaut en cas d'erreur
        }
      } else {
        const journalData = await journalService.loadUserJournal();
        const pendingOfflineMeals = journalService.getPendingOfflineMeals(journalData.id);
        const mealsWithPending = [...(journalData.meals || []), ...pendingOfflineMeals];

        setJournalId(journalData.id);
        setAllMeals(mealsWithPending);

        const goals = await settingsJournalService.getNutritionalGoals(journalData.id);
        setNutritionalGoals(goals);

        const advancedGoalsEnabled =
          isPremium
          && (
            goals.advancedMacroGoalsEnabled === true
            || goals.advancedMacroGoalsEnabled === 'true'
          );
        const savedSummaryMacros = Array.isArray(goals.summaryMacros) ? goals.summaryMacros : [];
        const sanitizedSummaryMacros = Array.from(
          new Set(
            savedSummaryMacros
              .filter((macroKey) => SUMMARY_FOCUSED_MACROS.has(macroKey))
              .filter((macroKey) => advancedGoalsEnabled || !ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey))
          )
        );
        setSummaryMacros(sanitizedSummaryMacros.length > 0 ? sanitizedSummaryMacros : [...SUMMARY_STANDARD_MACROS]);

        setHideUnavailableFoodMacros(Boolean(goals.hideUnavailableFoodMacros));
      }

      setIsInitialized(true);
    } catch (err) {
      console.error('Erreur chargement journal:', err);
      setError(err.message);
      showToast('Erreur lors du chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [demoData, friendUsername, isFriendView, isPremium, showToast]);

  const filterMealsForCurrentDate = useCallback(() => {
    const dateKey = getDateKey();
    const sortedMeals = [...allMeals].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const mealsWithFilteredFoods = sortedMeals.map(meal => ({
      ...meal,
      foods: (meal.foods || []).filter(food => {
        if (!food.createdAt) return false;
        const foodDateKey = food.createdAt.split('T')[0];
        return foodDateKey === dateKey;
      })
    }));

    setFilteredMeals(mealsWithFilteredFoods);
  }, [allMeals, getDateKey]);

  useEffect(() => {
    if (!isReadOnly) {
      favoriteFoodsService.listFavorites()
        .then((favorites) => {
          const normalizedFavorites = Array.isArray(favorites) ? favorites : [];
          setFavoriteFoods(normalizedFavorites);
        })
        .catch((error) => {
          console.warn('Impossible de charger les favoris depuis le journal:', error);
        });
    }
  }, [isReadOnly]);

  useEffect(() => {
    loadJournalFromAPI();
  }, [loadJournalFromAPI]);


  useEffect(() => {
    if (isInitialized) {
      filterMealsForCurrentDate();
    }
  }, [currentDate, allMeals, isInitialized, filterMealsForCurrentDate]);

  const syncQueuedMeals = useCallback(async () => {
    if (isReadOnly || !journalId || !isInitialized) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    if (isSyncingQueuedMealsRef.current) {
      return;
    }

    isSyncingQueuedMealsRef.current = true;

    try {
      const { syncedMeals, syncedMutations, failedCount } = await journalService.syncQueuedMeals(journalId);

      if (syncedMeals.length > 0 || syncedMutations > 0) {
        await loadJournalFromAPI();
        const mealPart = syncedMeals.length > 0
          ? `${syncedMeals.length} repas`
          : null;
        const mutationPart = syncedMutations > 0
          ? `${syncedMutations} modification${syncedMutations > 1 ? 's' : ''}`
          : null;
        const summary = [mealPart, mutationPart].filter(Boolean).join(' et ');
        showToast(`${summary} hors ligne synchronisé${syncedMeals.length + syncedMutations > 1 ? 's' : ''}`);
      } else if (failedCount > 0) {
        showToast('Certains repas hors ligne n’ont pas pu être synchronisés.');
      }
    } finally {
      isSyncingQueuedMealsRef.current = false;
    }
  }, [isReadOnly, journalId, isInitialized, loadJournalFromAPI, showToast]);

  useEffect(() => {
    if (isReadOnly || !journalId || !isInitialized) {
      return;
    }

    syncQueuedMeals();
  }, [isReadOnly, journalId, isInitialized, syncQueuedMeals]);

  useEffect(() => {
    const handleOnline = () => {
      showToast('Connexion rétablie. Synchronisation des repas en attente...');
      syncQueuedMeals();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncQueuedMeals, showToast]);

  useEffect(() => {
    if (isReadOnly) {
      return;
    }

    const handleGoalsChanged = () => {
      loadJournalFromAPI();
    };

    window.addEventListener('nutritionGoals:changed', handleGoalsChanged);

    return () => {
      window.removeEventListener('nutritionGoals:changed', handleGoalsChanged);
    };
  }, [isReadOnly, loadJournalFromAPI]);

  const normalizeMealName = useCallback((name) => {
    return (name || '').trim().toLowerCase();
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalConfig(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const changeDay = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + offset);
    setCurrentDate(newDate);
  };

  const handleJournalDateChange = useCallback((dateKey) => {
    setCurrentDate(fromDateKey(dateKey));
  }, [fromDateKey]);

  const getOffsetDateKey = useCallback((baseDate, offsetDays) => {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + offsetDays);
    return toDateKey(date);
  }, [toDateKey]);

  const openCopyCenter = useCallback(() => {
    setCopyMode('day_copy');
    setCopySourceDate(getOffsetDateKey(currentDate, -1));
    setCopyTargetDate(currentDateKey);
    setCopySourceMealId('');
    setCopyDestinationMealId('');
    setIsCopyDrawerOpen(true);
  }, [currentDate, getOffsetDateKey]);

  const closeCopyCenter = useCallback(() => {
    if (!isProcessing) {
      setIsCopyDrawerOpen(false);
    }
  }, [isProcessing]);

  const getMealById = useCallback((meals, mealId) => {
    if (!mealId) return null;
    return meals.find((meal) => String(meal.id) === String(mealId)) || null;
  }, []);

  const calculateNutrition = useCallback((baseNutrition, quantity) => {
    const calculated = buildAdjustedNutritionForDisplay(baseNutrition || {}, quantity);
    const kcalValue = calculated.calories ?? calculated['energy-kcal'];

    if (kcalValue !== undefined) {
      calculated.energy = kcalValue;
    }

    return calculated;
  }, []);

  const hasAnyExportableFoods = useMemo(
    () => allMeals.some((meal) => (meal.foods || []).some((food) => Boolean(food?.createdAt))),
    [allMeals]
  );

  const formatExportNumber = useCallback((value, digits = 2) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0.00';
    }
    return numeric.toFixed(digits);
  }, []);

  const buildJournalExportData = useCallback((dateRange = {}) => {
    const currentDateKey = getDateKey();
    const startRaw = dateRange.startDate || currentDateKey;
    const endRaw = dateRange.endDate || currentDateKey;
    const startDate = startRaw <= endRaw ? startRaw : endRaw;
    const endDate = startRaw <= endRaw ? endRaw : startRaw;

    const rows = [];
    const totals = {
      calories: 0,
      proteins: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      quantity: 0
    };
    const sortedMeals = [...allMeals].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    sortedMeals.forEach((meal) => {
      (meal.foods || []).forEach((food) => {
        if (!food.createdAt) {
          return;
        }
        const foodDateKey = food.createdAt.split('T')[0];
        if (foodDateKey < startDate || foodDateKey > endDate) {
          return;
        }

        const quantity = Number(food.quantity) || 0;
        const nutrition = calculateNutrition(food.macronutrients || {}, quantity);
        const row = {
          date: foodDateKey,
          mealName: meal.name || 'Repas',
          foodName: food.name,
          quantity,
          calories: Number(nutrition.energy || nutrition['energy-kcal'] || 0),
          proteins: Number(nutrition.proteins || 0),
          carbs: Number(nutrition.carbohydrates || 0),
          fat: Number(nutrition.fat || 0),
          fiber: Number(nutrition.fiber || 0),
          note: food.note || ''
        };

        rows.push(row);
        totals.quantity += row.quantity;
        totals.calories += row.calories;
        totals.proteins += row.proteins;
        totals.carbs += row.carbs;
        totals.fat += row.fat;
        totals.fiber += row.fiber;
      });
    });

    rows.sort((a, b) => {
      const dateDiff = a.date.localeCompare(b.date);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      const mealDiff = a.mealName.localeCompare(b.mealName);
      if (mealDiff !== 0) {
        return mealDiff;
      }
      return a.foodName.localeCompare(b.foodName);
    });

    const activeDays = new Set(rows.map((row) => row.date)).size;
    const safeDays = activeDays > 0 ? activeDays : 1;
    const averages = {
      calories: totals.calories / safeDays,
      proteins: totals.proteins / safeDays,
      carbs: totals.carbs / safeDays,
      fat: totals.fat / safeDays,
      fiber: totals.fiber / safeDays,
      quantity: totals.quantity / safeDays
    };

    return {
      rows,
      totals,
      averages,
      period: {
        startDate,
        endDate,
        activeDays
      }
    };
  }, [allMeals, calculateNutrition, getDateKey]);

  const sanitizeExcelText = useCallback((value) => {
    const normalized = String(value ?? '')
      .split('\0')
      .join('')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');

    // Protect against formula injection when users export/open in Excel.
    if (/^[=+\-@]/.test(normalized)) {
      return `'${normalized}`;
    }

    return normalized;
  }, []);

  const escapeHtml = useCallback((value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }, []);

  const exportJournalAsExcel = useCallback((dateRange = {}) => {
    const { rows, totals, averages, period } = buildJournalExportData(dateRange);
    if (rows.length === 0) {
      showToast('Aucun aliment à exporter pour la période sélectionnée.');
      return;
    }

    const toFixedNumber = (value) => Number(formatExportNumber(value));
    const headers = [
      'Date',
      'Repas',
      'Aliment',
      'Quantité (g)',
      'Calories (kcal)',
      'Protéines (g)',
      'Glucides (g)',
      'Lipides (g)',
      'Fibres (g)',
      'Note'
    ];

    const worksheetRows = rows.map((row) => ([
      sanitizeExcelText(row.date),
      sanitizeExcelText(row.mealName),
      sanitizeExcelText(row.foodName),
      toFixedNumber(row.quantity),
      toFixedNumber(row.calories),
      toFixedNumber(row.proteins),
      toFixedNumber(row.carbs),
      toFixedNumber(row.fat),
      toFixedNumber(row.fiber),
      sanitizeExcelText(row.note)
    ]));

    const totalRow = [
      'TOTAL',
      '',
      '',
      toFixedNumber(totals.quantity),
      toFixedNumber(totals.calories),
      toFixedNumber(totals.proteins),
      toFixedNumber(totals.carbs),
      toFixedNumber(totals.fat),
      toFixedNumber(totals.fiber),
      ''
    ];

    const worksheetData = [headers, ...worksheetRows, totalRow];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 34 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 40 }
    ];

    const numericColumns = [3, 4, 5, 6, 7, 8];
    for (let r = 1; r <= rows.length + 1; r += 1) {
      numericColumns.forEach((c) => {
        const ref = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[ref];
        if (cell && typeof cell.v === 'number') {
          cell.z = '0.00';
        }
      });
    }

    const periodLabel = period.startDate === period.endDate
      ? formatDateShort(period.startDate)
      : `${formatDateShort(period.startDate)} au ${formatDateShort(period.endDate)}`;

    const summaryData = [
      [`Résumé export ${SITE_NAME}`, ''],
      ['Période', periodLabel],
      ['Date début', period.startDate],
      ['Date fin', period.endDate],
      ['Jours actifs', period.activeDays],
      ['Lignes exportées', rows.length],
      ['', ''],
      ['Totaux période', ''],
      ['Calories (kcal)', toFixedNumber(totals.calories)],
      ['Protéines (g)', toFixedNumber(totals.proteins)],
      ['Glucides (g)', toFixedNumber(totals.carbs)],
      ['Lipides (g)', toFixedNumber(totals.fat)],
      ['Fibres (g)', toFixedNumber(totals.fiber)],
      ['Quantité (g)', toFixedNumber(totals.quantity)],
      ['', ''],
      ['Moyennes / jour actif', ''],
      ['Calories (kcal)', toFixedNumber(averages.calories)],
      ['Protéines (g)', toFixedNumber(averages.proteins)],
      ['Glucides (g)', toFixedNumber(averages.carbs)],
      ['Lipides (g)', toFixedNumber(averages.fat)],
      ['Fibres (g)', toFixedNumber(averages.fiber)],
      ['Quantité (g)', toFixedNumber(averages.quantity)]
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 32 }, { wch: 34 }];
    for (let r = 8; r <= 13; r += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 1 });
      if (summarySheet[cellRef] && typeof summarySheet[cellRef].v === 'number') {
        summarySheet[cellRef].z = '0.00';
      }
    }
    for (let r = 16; r <= 21; r += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 1 });
      if (summarySheet[cellRef] && typeof summarySheet[cellRef].v === 'number') {
        summarySheet[cellRef].z = '0.00';
      }
    }

    const workbook = XLSX.utils.book_new();
    const worksheetName = `Journal_${getDateKey()}`
      .replaceAll('\\', '_')
      .replaceAll('/', '_')
      .replaceAll('*', '_')
      .replaceAll('?', '_')
      .replaceAll(':', '_')
      .replaceAll('[', '_')
      .replaceAll(']', '_')
      .slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName || 'Journal');
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resume');

    const exportDateSuffix = period.startDate === period.endDate
      ? period.startDate
      : `${period.startDate}_au_${period.endDate}`;
    XLSX.writeFile(workbook, `journal-${exportDateSuffix}.xlsx`, { compression: true });
    showToast('Export Excel téléchargé');
  }, [buildJournalExportData, formatDateShort, formatExportNumber, getDateKey, sanitizeExcelText, showToast]);

  const exportJournalAsPdf = useCallback((dateRange = {}) => {
    const { rows, totals, averages, period } = buildJournalExportData(dateRange);
    if (rows.length === 0) {
      showToast('Aucun aliment à exporter pour la période sélectionnée.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1120,height=760');
    if (!printWindow) {
      showToast('Impossible d’ouvrir la fenêtre PDF. Autorisez les pop-up puis réessayez.');
      return;
    }

    const tableRowsHtml = rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.mealName)}</td>
        <td>${escapeHtml(row.foodName)}</td>
        <td class="num">${formatExportNumber(row.quantity)}</td>
        <td class="num">${formatExportNumber(row.calories)}</td>
        <td class="num">${formatExportNumber(row.proteins)}</td>
        <td class="num">${formatExportNumber(row.carbs)}</td>
        <td class="num">${formatExportNumber(row.fat)}</td>
        <td class="num">${formatExportNumber(row.fiber)}</td>
        <td>${escapeHtml(row.note).replace(/\n/g, '<br/>')}</td>
      </tr>
    `).join('');

    const generatedAt = new Date().toLocaleString('fr-FR');
    const periodLabel = period.startDate === period.endDate
      ? formatDateShort(period.startDate)
      : `${formatDateShort(period.startDate)} au ${formatDateShort(period.endDate)}`;

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Export journal ${escapeHtml(period.startDate)}-${escapeHtml(period.endDate)}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; color: #1f2937; }
    h1 { margin: 0 0 8px; font-size: 22px; }
    .meta { margin: 0 0 18px; color: #4b5563; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    th, td { border: 1px solid #d1d5db; padding: 8px; font-size: 12px; vertical-align: top; }
    th { background: #f3f4f6; text-align: left; }
    td.num { text-align: right; white-space: nowrap; }
    tfoot td { font-weight: 700; background: #f9fafb; }
    @page { size: A4 landscape; margin: 12mm; }
  </style>
</head>
<body>
  <h1>Journal alimentaire</h1>
  <p class="meta">Période: ${escapeHtml(periodLabel)} | Exporté le: ${escapeHtml(generatedAt)} | Jours actifs: ${period.activeDays}</p>
  <div style="margin: 0 0 12px 0; font-size: 12px; color: #374151;">
    <strong>Total période:</strong>
    Calories ${formatExportNumber(totals.calories)} kcal,
    Protéines ${formatExportNumber(totals.proteins)} g,
    Glucides ${formatExportNumber(totals.carbs)} g,
    Lipides ${formatExportNumber(totals.fat)} g,
    Fibres ${formatExportNumber(totals.fiber)} g
    <br />
    <strong>Moyenne / jour actif:</strong>
    Calories ${formatExportNumber(averages.calories)} kcal,
    Protéines ${formatExportNumber(averages.proteins)} g,
    Glucides ${formatExportNumber(averages.carbs)} g,
    Lipides ${formatExportNumber(averages.fat)} g,
    Fibres ${formatExportNumber(averages.fiber)} g
  </div>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Repas</th>
        <th>Aliment</th>
        <th>Quantité (g)</th>
        <th>Calories (kcal)</th>
        <th>Protéines (g)</th>
        <th>Glucides (g)</th>
        <th>Lipides (g)</th>
        <th>Fibres (g)</th>
        <th>Note</th>
      </tr>
    </thead>
    <tbody>${tableRowsHtml}</tbody>
    <tfoot>
      <tr>
        <td colspan="3">TOTAL</td>
        <td class="num">${formatExportNumber(totals.quantity)}</td>
        <td class="num">${formatExportNumber(totals.calories)}</td>
        <td class="num">${formatExportNumber(totals.proteins)}</td>
        <td class="num">${formatExportNumber(totals.carbs)}</td>
        <td class="num">${formatExportNumber(totals.fat)}</td>
        <td class="num">${formatExportNumber(totals.fiber)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => {
      printWindow.print();
    }, 250);
    showToast('Fenêtre d’impression ouverte. Choisissez "Enregistrer en PDF".');
  }, [buildJournalExportData, escapeHtml, formatDateShort, formatExportNumber, showToast]);

  const openPremiumExportModal = useCallback(() => {
    setModalConfig({
      title: 'Export réservé aux comptes Premium',
      message: 'Les exports PDF/Excel avancés sont disponibles avec un abonnement Premium ou Pro.',
      confirmText: 'Voir les offres',
      cancelText: 'Fermer',
      size: 'sm',
      onConfirm: () => {
        window.location.href = '/offers';
        closeModal();
      },
      onCancel: closeModal
    });
    setShowModal(true);
  }, [closeModal]);

  const openExportPeriodModal = useCallback((format) => {
    const currentDateKey = getDateKey();

    const ExportPeriodForm = ({ onSubmit }) => {
      const [startDate, setStartDate] = useState(currentDateKey);
      const [endDate, setEndDate] = useState(currentDateKey);
      const [formError, setFormError] = useState('');

      const applyPreset = (days) => {
        const end = currentDateKey;
        const start = new Date(fromDateKey(currentDateKey));
        start.setDate(start.getDate() - (days - 1));
        setStartDate(toDateKey(start));
        setEndDate(end);
        setFormError('');
      };

      const handleSubmit = (event) => {
        event.preventDefault();

        if (!startDate || !endDate) {
          setFormError('Sélectionnez une date de début et de fin.');
          return;
        }

        onSubmit({ startDate, endDate });
      };

      return (
        <form id="export-period-form" className="modal-export-period-form" onSubmit={handleSubmit}>
          <div className="modal-export-period-presets">
            <button type="button" onClick={() => applyPreset(7)}>7 jours</button>
            <button type="button" onClick={() => applyPreset(30)}>30 jours</button>
            <button type="button" onClick={() => applyPreset(90)}>90 jours</button>
          </div>

          <label className="modal-export-period-field" htmlFor="export-period-start">
            <span>Date de début</span>
            <SharedDatePicker
              inputId="export-period-start"
              className="modal-export-period-picker"
              value={startDate}
              onChange={(dateKey) => {
                setStartDate(dateKey);
                setFormError('');
              }}
              maxDateKey={endDate || undefined}
              showQuickActions={false}
            />
          </label>

          <label className="modal-export-period-field" htmlFor="export-period-end">
            <span>Date de fin</span>
            <SharedDatePicker
              inputId="export-period-end"
              className="modal-export-period-picker"
              value={endDate}
              onChange={(dateKey) => {
                setEndDate(dateKey);
                setFormError('');
              }}
              minDateKey={startDate || undefined}
              showQuickActions={false}
            />
          </label>

          {formError && <p className="modal-export-period-error">{formError}</p>}
        </form>
      );
    };

    setModalConfig({
      title: format === 'excel' ? 'Exporter le journal en Excel' : 'Exporter le journal en PDF',
      message: 'Choisissez la période à exporter.',
      children: (
        <ExportPeriodForm
          onSubmit={(dateRange) => {
            if (format === 'excel') {
              exportJournalAsExcel(dateRange);
            } else {
              exportJournalAsPdf(dateRange);
            }
            closeModal();
          }}
        />
      ),
      onConfirm: () => {
        document.getElementById('export-period-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      },
      onCancel: closeModal,
      confirmText: format === 'excel' ? 'Télécharger Excel' : 'Ouvrir version PDF',
      cancelText: 'Annuler',
      size: 'sm',
      className: 'modal-export-period'
    });
    setShowModal(true);
  }, [closeModal, exportJournalAsExcel, exportJournalAsPdf, fromDateKey, getDateKey, toDateKey]);

  const handleExportRequest = useCallback((format) => {
    if (!isPremium) {
      openPremiumExportModal();
      return;
    }

    openExportPeriodModal(format);
  }, [isPremium, openExportPeriodModal, openPremiumExportModal]);

  const addMeal = async () => {
    if (!journalId) {
      showToast('Journal indisponible. Réessayez dans quelques secondes.');
      return;
    }

    try {
      const newMeal = await journalService.addMeal(journalId, {
        name: "Nouveau repas",
        mealTime: getCurrentLocalTimeValue(),
        foods: []
      });

      setAllMeals(prev => [...prev, newMeal]);

      if (isPendingOfflineMeal(newMeal)) {
        showToast('Hors ligne: repas ajouté en attente de synchronisation.');
        return;
      }

      showToast('Repas ajouté avec succès');
    } catch (error) {
      console.error('Erreur ajout repas:', error);
      showToast('Erreur lors de l\'ajout du repas');
    }
  };

  const removeMeal = (mealId, mealName = 'ce repas') => {
    const safeMealName = (mealName || 'ce repas').trim();
    setModalConfig({
      title: 'Supprimer ce repas ?',
      message: `Le repas "${safeMealName}" et ses aliments seront supprimés.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmVariant: 'danger',
      size: 'sm',
      onConfirm: async () => {
        if (isPendingOfflineMeal(mealId)) {
          const removed = journalService.removePendingMeal(mealId);
          if (removed) {
            setAllMeals(prev => prev.filter(meal => meal.id !== mealId));
            showToast('Repas hors ligne supprimé');
          } else {
            showToast('Impossible de supprimer ce repas hors ligne');
          }
          closeModal();
          return;
        }

        try {
          const result = await journalService.deleteMeal(mealId, journalId);
          setAllMeals(prev => prev.filter(meal => meal.id !== mealId));
          showToast(result?.queued ? 'Repas supprimé (en attente de synchronisation).' : 'Repas supprimé');
          closeModal();
        } catch (error) {
          console.error('Erreur suppression repas:', error);
          showToast('Erreur lors de la suppression');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const renameMeal = useCallback(async (mealId, newName) => {
    if (isPendingOfflineMeal(mealId)) {
      const renamed = journalService.renamePendingMeal(mealId, newName);
      if (renamed) {
        setAllMeals(prev => prev.map((meal) => (
          meal.id === mealId
            ? { ...meal, name: newName }
            : meal
        )));
      }
      return { success: renamed, queued: true };
    }

    try {
      const result = await journalService.updateMeal(mealId, { name: newName }, journalId);

      setAllMeals(prev => prev.map((meal) => (
        meal.id === mealId
          ? { ...meal, name: newName }
          : meal
      )));

      return { success: true, queued: Boolean(result?.queued) };
    } catch (error) {
      console.error('Erreur renommage repas:', error);
      showToast('Erreur lors du renommage');
      return { success: false, queued: false };
    }
  }, [isPendingOfflineMeal, journalId, showToast]);

  const startRenamingMeal = useCallback((meal) => {
    if (!meal?.id) return;

    setEditingMealId(meal.id);
    setMealNameDrafts(prev => ({
      ...prev,
      [meal.id]: meal.name || ''
    }));

    requestAnimationFrame(() => {
      const input = mealNameInputRefs.current[meal.id];
      if (input) {
        input.focus();
        input.select();
      }
    });
  }, []);

  const cancelRenamingMeal = useCallback((mealId) => {
    setEditingMealId(prev => (prev === mealId ? null : prev));
    setMealNameDrafts(prev => {
      if (!(mealId in prev)) return prev;
      const next = { ...prev };
      delete next[mealId];
      return next;
    });
  }, []);

  const saveMealName = useCallback(async (meal) => {
    if (!meal?.id) return;

    const draftName = (mealNameDrafts[meal.id] ?? meal.name ?? '').trim();
    const finalName = draftName || 'Nouveau repas';
    const currentName = (meal.name || '').trim();

    if (finalName === currentName) {
      cancelRenamingMeal(meal.id);
      return;
    }

    const result = await renameMeal(meal.id, finalName);
    if (result?.success) {
      showToast(result.queued ? 'Nom du repas enregistré (synchronisation en attente).' : 'Nom du repas mis à jour');
      cancelRenamingMeal(meal.id);
    }
  }, [mealNameDrafts, cancelRenamingMeal, renameMeal, showToast]);

  const startEditingMealTime = useCallback((meal) => {
    if (!meal?.id || isReadOnly) return;

    setEditingMealTimeId(meal.id);
    setMealTimeDrafts((prev) => ({
      ...prev,
      [meal.id]: String(meal.mealTime || '').slice(0, 5) || getCurrentLocalTimeValue(),
    }));
  }, [isReadOnly]);

  const cancelEditingMealTime = useCallback((mealId) => {
    setEditingMealTimeId((prev) => (prev === mealId ? null : prev));
    setMealTimeDrafts((prev) => {
      if (!(mealId in prev)) return prev;
      const next = { ...prev };
      delete next[mealId];
      return next;
    });
  }, []);

  const saveMealTime = useCallback(async (meal) => {
    if (!meal?.id || savingMealTimeId === meal.id) return;

    const draft = String(mealTimeDrafts[meal.id] || '').trim();
    if (draft && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(draft)) {
      showToast('Saisissez une heure valide.');
      return;
    }

    const mealTime = draft || null;
    if (mealTime === (meal.mealTime || null)) {
      cancelEditingMealTime(meal.id);
      return;
    }

    setSavingMealTimeId(meal.id);
    try {
      const result = await journalService.updateMeal(meal.id, { mealTime }, journalId);
      if (result?.pending && !result?.queued) {
        throw new Error('Impossible de mettre à jour ce repas hors ligne.');
      }
      setAllMeals((prev) => prev.map((item) => (
        String(item.id) === String(meal.id)
          ? { ...item, mealTime }
          : item
      )));
      showToast(result?.queued ? 'Heure enregistrée (synchronisation en attente).' : 'Heure du repas mise à jour.');
      cancelEditingMealTime(meal.id);
    } catch (error) {
      console.error('Erreur modification de l’heure du repas:', error);
      showToast('Erreur lors de la modification de l’heure.');
    } finally {
      setSavingMealTimeId(null);
    }
  }, [mealTimeDrafts, savingMealTimeId, journalId, cancelEditingMealTime, showToast]);

  const findExistingFoodByBarcode = (barcode, meals) => {
    if (!barcode || !Array.isArray(meals)) {
      return null;
    }

    const normalizedBarcode = String(barcode).trim();
    if (!normalizedBarcode) {
      return null;
    }

    for (const meal of meals) {
      if (!Array.isArray(meal.foods)) {
        continue;
      }

      for (const food of meal.foods) {
        const foodBarcode = String(food?.barcode || food?.code || '').trim();
        if (foodBarcode === normalizedBarcode) {
          return {
            food,
            mealId: meal.id,
            mealIndex: meals.indexOf(meal),
          };
        }
      }
    }

    return null;
  };

  // Handler pour l'édition d'un produit sur OpenFoodFacts
  const handleEditOpenFoodFactsProduct = async (barcode, editedData, meal) => {
    if (foodMutationInFlightRef.current) {
      showToast('Une action est deja en cours, patientez un instant.');
      return;
    }

    foodMutationInFlightRef.current = true;
    setIsFoodMutationLoading(true);

    try {
      let freshOffData = null;

      try {
        const result = await openFoodFactsService.addProduct({
          barcode: barcode,
          name: editedData.name,
          brand: editedData.brand || '',
          _newBrand: editedData._newBrand || editedData.brand || '',
          quantity: editedData.quantity,
          macronutrients: editedData.macronutrients,
          ingredients: Array.isArray(editedData.ingredients)
            ? editedData.ingredients.join(', ')
            : (typeof editedData.ingredients === 'string' ? editedData.ingredients : ''),
          images: editedData.images || {},
        });
        freshOffData = result?.data || result || null;
      } catch (offError) {
        // Erreur OFF (timeout, 502, 504...) → on n'ajoute PAS au journal
        showToast(offError?.message || 'Erreur lors de la mise à jour sur OpenFoodFacts');
        return;
      }

      // Récupère la version à jour depuis OFF (addProduct ne renvoie pas toujours le produit complet)
      try {
        const refetched = await openFoodFactsService.getProductByCode({ code: barcode });
        if (refetched) {
          freshOffData = refetched;
        }
      } catch {
        // on garde freshOffData de la réponse addProduct si le refetch échoue
      }

      showToast('Produit mis à jour sur OpenFoodFacts avec succès');

      await journalService.addFoodItem(meal.id, {
        barcode: editedData.barcode,
        name: editedData.name,
        quantity: editedData.quantity,
        macronutrients: editedData.macronutrients,
        ingredients: editedData.ingredients || [],
        note: editedData.note || '',
        openFoodFactsData: freshOffData || editedData.openFoodFactsData || null,
        targetDate: getDateKey(),
        sortOrder: getNextFoodSortOrder(meal),
      });

      rememberFoodNote({
        barcode: barcode || '',
        name: editedData.name,
        brand: editedData._newBrand || '',
        note: editedData.note || '',
      });

      if (barcode) {
        processedImageKeysRef.current.delete(barcode);
        setFoodImageCache((prev) => {
          if (!(barcode in prev)) return prev;
          const next = { ...prev };
          delete next[barcode];
          return next;
        });
        setInvalidatedBarcode(barcode);
      }

      await loadJournalFromAPI();
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la mise à jour OpenFoodFacts:', error);
      showToast('Erreur lors de la mise à jour sur OpenFoodFacts');
    } finally {
      foodMutationInFlightRef.current = false;
      setIsFoodMutationLoading(false);
    }
  };

  // Handler pour l'édition d'un aliment existant
  const handleEditFoodSubmit = async (foodId, foodData, meal) => {
    if (foodMutationInFlightRef.current) {
      showToast('Une action est deja en cours, patientez un instant.');
      return;
    }

    foodMutationInFlightRef.current = true;
    setIsFoodMutationLoading(true);

    try {
      const payload = {
        quantity: foodData.quantity,
      };

      if (foodData.createdAt) {
        payload.createdAt = foodData.createdAt;
      }

      const hasTargetMeal = foodData.mealId && String(foodData.mealId).trim() !== '';
      const isMealChanged = hasTargetMeal && String(foodData.mealId) !== String(meal?.id);
      if (isMealChanged) {
        payload.meal = `/api/meals/${foodData.mealId}`;
        const destinationMeal = allMeals.find((item) => String(item.id) === String(foodData.mealId));
        if (destinationMeal) {
          payload.sortOrder = getNextFoodSortOrder(destinationMeal);
        }
      }

      await journalService.updateFoodItem(foodId, payload);
      showToast(isMealChanged ? 'Aliment mis à jour et déplacé' : 'Aliment mis à jour');
      await loadJournalFromAPI();
      closeModal();
    } catch (error) {
      console.error('Erreur modification aliment:', error);
      showToast(error.message || 'Erreur lors de la modification');
    } finally {
      foodMutationInFlightRef.current = false;
      setIsFoodMutationLoading(false);
    }
  };

  // Handler pour l'ajout d'un nouvel aliment
  const handleAddFoodSubmit = async (foodData, meal) => {
    if (foodMutationInFlightRef.current) {
      showToast('Une action est deja en cours, patientez un instant.');
      return;
    }

    foodMutationInFlightRef.current = true;
    setIsFoodMutationLoading(true);

    try {
      if (!meal?.id) {
        showToast('Repas introuvable');
        return;
      }

      // Vérification des doublons par code-barres dans le journal du jour
      if (foodData.barcode) {
        const existingFood = findExistingFoodByBarcode(foodData.barcode, filteredMeals);
        if (existingFood) {
          showToast('Cet aliment existe déjà dans votre journal du jour. Bascule en mode modification.');
          closeModal();
          // Attendre la fermeture du modal avant de réouvrir
          setTimeout(() => {
            const existingMeal = filteredMeals[existingFood.mealIndex];
            const initialFood = existingFood.food;

            setModalConfig({
              title: 'Modifier un aliment',
              message: 'Ajustez la quantité ou choisissez un autre repas.',
              children: (
                <MealItemEditor
                  initialFood={initialFood}
                  onSubmit={(foodData) => handleEditFoodSubmit(existingFood.food.id, foodData, existingMeal)}
                  sourceMealId={existingMeal?.id}
                  availableMeals={filteredMeals.filter((candidateMeal) => !isPendingOfflineMeal(candidateMeal))}
                  journalDateKey={getDateKey()}
                />
              ),
              onConfirm: () => {
                document.getElementById('edit-food-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              },
              onCancel: closeModal,
              confirmText: 'Enregistrer',
              cancelText: 'Annuler',
              size: 'lg',
              className: 'modal-wide modal-food-edit'
            });

            setShowModal(true);
          }, 300);
          return;
        }
      }

      // _offExists = true  → produit trouvé sur OFF, ajout direct sans addProduct
      // _offExists = false → vérifier sur OFF, puis tenter addProduct si inexistant
      let offAlreadyExists = Boolean(foodData._offExists);
      let offPublished = offAlreadyExists; // already on OFF = considered "published"

      if (!offAlreadyExists && foodData.barcode) {
        // Vérifier si le produit existe déjà sur OpenFoodFacts avant de tenter la publication
        try {
          const existingProduct = await openFoodFactsService.getProductByCode({ code: foodData.barcode });
          if (existingProduct) {
            offAlreadyExists = true;
            offPublished = true;
            foodData.openFoodFactsData = existingProduct;

            // Aliment existe sur OpenFoodFacts - afficher formulaire d'édition
            const productName = existingProduct.product_name || existingProduct.product_name_fr || foodData.name || 'Cet aliment';

            closeModal();

            setTimeout(() => {
              setModalConfig({
                title: 'Modifier l\'aliment sur OpenFoodFacts',
                message: `L'aliment "${productName}" existe déjà sur OpenFoodFacts. Modifiez ses informations ci-dessous.`,
                children: (
                  <EditFoodForm
                    onSubmit={(editedData) => handleEditOpenFoodFactsProduct(foodData.barcode, editedData, meal)}
                    isMobileViewport={isMobileViewport}
                    initialData={{
                      barcode: foodData.barcode,
                      name: existingProduct.product_name || existingProduct.product_name_fr || foodData.name,
                      brand: existingProduct.brands || '',
                      quantity: existingProduct.quantity || 100,
                      macronutrients: existingProduct.nutriments || {},
                      ingredients: existingProduct.ingredients_text
                        ? existingProduct.ingredients_text.split(',').map((part) => part.trim()).filter(Boolean)
                        : [],
                    }}
                    isEditMode={true}
                  />
                ),
                onConfirm: () => {
                  document.getElementById('edit-food-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                },
                onCancel: closeModal,
                confirmText: 'Mettre à jour sur OpenFoodFacts',
                cancelText: 'Annuler',
                size: 'lg',
                className: 'modal-wide modal-off-edit'
              });
              setShowModal(true);
            }, 300);
            return;
          }
        } catch (checkError) {
          // Si la vérification échoue (timeout, erreur réseau), on continue avec la tentative de publication
          console.warn('Erreur lors de la vérification OpenFoodFacts:', checkError.message);
        }
      }

      if (!offAlreadyExists) {
        // Nouveau produit : tentative de contribution vers OpenFoodFacts
        if (!foodData.barcode) {
          showToast('Code-barres requis pour publier sur OpenFoodFacts');
          return;
        }
        try {
          await openFoodFactsService.addProduct({
            barcode: foodData.barcode,
            name: foodData.name,
            brand: foodData.brand || foodData._newBrand || '',
            _newBrand: foodData._newBrand || '',
            quantity: foodData.quantity,
            macronutrients: foodData.macronutrients,
            ingredients: foodData.ingredients,
            images: foodData.images || {},
          });
          offPublished = true;
        } catch (offError) {
          const message = offError?.message || '';
          const safeToSkip =
            message.includes('existant') ||
            message.includes('Impossible de vérifier') ||
            message.includes('Identifiants OpenFoodFacts') ||
            message.includes('timeout') ||
            message.includes('Gateway Timeout') ||
            message.includes('ne répond pas') ||
            message.includes('Bad Gateway');
          if (!safeToSkip) {
            throw offError;
          }
          showToast(message);
        }
      }

      await journalService.addFoodItem(meal.id, {
        barcode: foodData.barcode,
        name: foodData.name,
        quantity: foodData.quantity,
        macronutrients: foodData.macronutrients,
        ingredients: foodData.ingredients || [],
        note: foodData.note || '',
        openFoodFactsData: foodData.openFoodFactsData || null,
        targetDate: getDateKey(),
        sortOrder: getNextFoodSortOrder(meal),
      });

      rememberFoodNote({
        barcode: foodData.barcode || '',
        name: foodData.name,
        brand: foodData._newBrand || '',
        note: foodData.note || '',
      });

      await loadJournalFromAPI();

      if (offAlreadyExists) {
        showToast('Aliment ajouté au journal');
      } else {
        showToast(
          offPublished
            ? 'Aliment ajouté au journal et proposé à OpenFoodFacts'
            : 'Aliment ajouté au journal (publication OpenFoodFacts ignorée)'
        );
      }

      closeModal();
    } catch (error) {
      console.error('Erreur ajout aliment:', error);
      showToast(error.message || 'Erreur lors de l\'ajout');
    } finally {
      foodMutationInFlightRef.current = false;
      setIsFoodMutationLoading(false);
    }
  };

  const handleAddNewFoodFromSearch = (searchTerm) => {
    const mealIndex = searchState.mealIndex;
    closeSearch();

    if (mealIndex === null || mealIndex === undefined) return;
    const meal = filteredMeals[mealIndex];

    if (!meal?.id) {
      showToast('Repas introuvable');
      return;
    }

    if (isPendingOfflineMeal(meal)) {
      showToast('Ce repas est en attente de synchronisation. Reconnectez-vous pour ajouter des aliments.');
      return;
    }

    setTimeout(() => {
      setModalConfig({
        title: 'Ajouter un nouveau produit',
        message: searchTerm
          ? `Aucun résultat pour « ${searchTerm} ». Renseignez ses informations ci-dessous.`
          : undefined,
        children: (
          <AddFoodForm
            onSubmit={(foodData) => handleAddFoodSubmit(foodData, meal)}
            isMobileViewport={isMobileViewport}
            prefilledName={searchTerm}
          />
        ),
        onConfirm: () => {
          document.getElementById('add-food-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        },
        onCancel: closeModal,
        confirmText: 'Ajouter',
        cancelText: 'Annuler',
        size: 'lg',
        className: 'modal-wide modal-food-add'
      });
      setShowModal(true);
    }, 300); // même délai que tes autres transitions modal après fermeture
  };

  const addFoodToMeal = (mealId, mealIndex, foodId = null) => {
    const meal = filteredMeals[mealIndex];

    if (isPendingOfflineMeal(meal)) {
      showToast('Ce repas est en attente de synchronisation. Reconnectez-vous pour ajouter des aliments.');
      return;
    }

    const initialFood = foodId !== null
      ? meal.foods.find(f => f.id === foodId)
      : {};

    setModalConfig({
      title: foodId !== null ? 'Modifier un aliment' : 'Ajouter un aliment',
      message: foodId !== null
        ? 'Ajustez la quantité ou choisissez un autre repas.'
        : undefined,
      children: foodId !== null ? (
        <MealItemEditor
          initialFood={initialFood}
          onSubmit={(foodData) => handleEditFoodSubmit(foodId, foodData, meal)}
          sourceMealId={meal?.id}
          availableMeals={filteredMeals.filter((candidateMeal) => !isPendingOfflineMeal(candidateMeal))}
          journalDateKey={getDateKey()}
        />
      ) : (
        <AddFoodForm
          onSubmit={(foodData) => handleAddFoodSubmit(foodData, meal)}
          isMobileViewport={isMobileViewport}
        />
      ),
      onConfirm: () => {
        const formId = foodId !== null ? 'edit-food-form' : 'add-food-form';
        document.getElementById(formId)?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      },
      onCancel: closeModal,
      confirmText: foodId !== null ? 'Enregistrer' : 'Ajouter',
      cancelText: 'Annuler',
      size: 'lg',
      className: `modal-wide ${foodId !== null ? 'modal-food-edit' : 'modal-food-add'}`
    });

    setShowModal(true);
  };

  const removeFood = (foodId, foodName = 'cet aliment') => {
    const safeFoodName = (foodName || 'cet aliment').trim();
    setModalConfig({
      title: 'Supprimer cet aliment ?',
      message: `"${safeFoodName}" sera retiré du repas.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmVariant: 'danger',
      size: 'sm',
      onConfirm: async () => {
        try {
          await journalService.deleteFoodItem(foodId);
          await loadJournalFromAPI();
          showToast('Aliment supprimé');
          closeModal();
        } catch (error) {
          console.error('Erreur suppression aliment:', error);
          showToast('Erreur lors de la suppression');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const editFoodNote = (foodId, currentNote, foodName) => {
    const safeFoodName = (foodName || 'cet aliment').trim();
    const noteTitleTarget = safeFoodName.length > 56 ? `${safeFoodName.slice(0, 56)}…` : safeFoodName;

    const handleNoteSubmit = async (noteText) => {
      try {
        await journalService.updateFoodItem(foodId, { note: noteText.trim() });
        rememberFoodNote({
          barcode: "",
          name: safeFoodName,
          brand: "",
          note: noteText.trim(),
        });
        await loadJournalFromAPI();
        showToast(noteText.trim() ? 'Note mise à jour' : 'Note supprimée');
        closeModal();
      } catch (error) {
        console.error('Erreur mise à jour note:', error);
        showToast('Erreur lors de la mise à jour de la note');
      }
    };

    setModalConfig({
      title: 'Modifier la note',
      message: `Aliment: ${noteTitleTarget}`,
      children: (
        <FoodNoteForm
          foodName={safeFoodName}
          initialNote={currentNote}
          onSubmit={handleNoteSubmit}
          isMobileViewport={isMobileViewport}
        />
      ),
      onConfirm: () => {
        document.getElementById('note-editor-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      },
      onCancel: closeModal,
      confirmText: 'Enregistrer la note',
      cancelText: 'Annuler',
      size: 'md',
      className: 'modal-food-note'
    });

    setShowModal(true);
  };

  const handleEditProductFromSearch = (product) => {
    closeSearch();

    const mealIndex = searchState.mealIndex;
    if (mealIndex === null || mealIndex === undefined) return;
    const meal = filteredMeals[mealIndex];

    if (!meal?.id) {
      showToast('Repas introuvable');
      return;
    }

    const barcode = product.code || product.barcode || '';
    const productName = product.product_name || product.product_name_fr || 'Cet aliment';

    setTimeout(() => {
      setModalConfig({
        title: 'Modifier l\'aliment sur OpenFoodFacts',
        message: `Modifiez les informations de "${productName}" ci-dessous.`,
        children: (
          <EditFoodForm
            onSubmit={(editedData) => handleEditOpenFoodFactsProduct(barcode, editedData, meal)}
            isMobileViewport={isMobileViewport}
            initialData={{
              barcode: barcode,
              name: product.product_name || product.product_name_fr || '',
              brand: product.brands || '',
              quantity: product.quantity || 100,
              macronutrients: product.nutriments || {},
              ingredients: product.ingredients_text
                ? product.ingredients_text.split(',').map((part) => part.trim()).filter(Boolean)
                : [],
            }}
            isEditMode={true}
          />
        ),
        onConfirm: () => {
          document.getElementById('edit-food-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        },
        onCancel: closeModal,
        confirmText: 'Mettre à jour sur OpenFoodFacts',
        cancelText: 'Annuler',
        size: 'lg',
        className: 'modal-wide modal-off-edit'
      });
      setShowModal(true);
    }, 300);
  };

  const selectProduct = async (foodData) => {
    const mealIndex = searchState.mealIndex;
    if (mealIndex === null || mealIndex === undefined) return;
    const meal = filteredMeals[mealIndex];

    if (foodMutationInFlightRef.current) {
      showToast('Ajout en cours, merci de patienter.');
      return;
    }

    foodMutationInFlightRef.current = true;
    setIsFoodMutationLoading(true);

    try {
      const openFoodFactsData = foodData.openFoodFactsData
        || foodData.openFoodFacts
        || foodData.offData
        || null;
      const foodDataWithDate = {
        name: foodData.name,
        quantity: foodData.quantity,
        macronutrients: foodData.macronutrients,
        ingredients: foodData.ingredients || [],
        note: foodData.note || '',
        barcode: foodData.barcode || '',
        openFoodFactsData
      };

      if (!meal?.id) {
        showToast('Repas introuvable');
        return;
      }

      if (isPendingOfflineMeal(meal)) {
        showToast('Ce repas est en attente de synchronisation. Reconnectez-vous pour ajouter des aliments.');
        return;
      }

      await journalService.addFoodItem(meal.id, {
        barcode: foodDataWithDate.barcode,
        name: foodDataWithDate.name,
        quantity: foodDataWithDate.quantity,
        macronutrients: foodDataWithDate.macronutrients,
        ingredients: foodDataWithDate.ingredients,
        note: foodDataWithDate.note,
        openFoodFactsData: foodDataWithDate.openFoodFactsData,
        targetDate: getDateKey(),
        sortOrder: getNextFoodSortOrder(meal)
      });

      rememberFoodNote({
        barcode: foodDataWithDate.barcode,
        name: foodDataWithDate.name,
        brand: '',
        note: foodDataWithDate.note,
      });

      await loadJournalFromAPI();
      closeSearch();
      showToast(`${foodData.name} ajouté au journal`);
    } catch (error) {
      console.error('Erreur ajout aliment:', error);
      showToast(error.message || 'Erreur lors de l\'ajout');
    } finally {
      foodMutationInFlightRef.current = false;
      setIsFoodMutationLoading(false);
    }
  };

  const searchFoodInDB = useCallback((mealIndex) => {
    setSearchState({
      visible: true,
      mealIndex,
      query: '',
      loading: false,
      results: [],
      selectedIndex: 0
    });
  }, []);

  const closeSearch = useCallback(() => {
    setSearchState({
      visible: false,
      mealIndex: null,
      query: '',
      loading: false,
      results: [],
      selectedIndex: 0
    });
  }, []);

  const performSearch = useCallback(async (query) => {
    const searchTerm = query.trim();
    if (!searchTerm) {
      showToast('Veuillez saisir un terme de recherche');
      return;
    }

    setSearchState(prev => ({ ...prev, loading: true, results: [] }));

    try {
      const data = await openFoodFactsService.searchFoods({
        query: searchTerm,
        page: 1,
        pageSize: 20
      });
      const products = (data.products || []).filter(p => p.product_name && p.nutriments).slice(0, 15);

      setSearchState(prev => ({
        ...prev,
        loading: false,
        results: products,
        selectedIndex: products.length > 0 ? 0 : -1
      }));

      if (data?.degraded && products.length === 0) {
        showToast(data?.message || 'Le service OpenFoodFacts est temporairement indisponible.');
      }
    } catch (err) {
      console.error(err);
      setSearchState(prev => ({ ...prev, loading: false, results: [] }));
      showToast(err?.message || 'Erreur lors de la recherche');
    }
  }, [showToast]);

  const debouncedSearch = useCallback((query) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      performSearch(query);
    }, 400);
  }, [performSearch]);

  useEffect(() => {
    if (searchState.visible && searchState.query && searchState.query.trim() !== '') {
      debouncedSearch(searchState.query);
    }
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchState.query, searchState.visible, debouncedSearch]);

  const currentDateKey = getDateKey();

  const openCopyCenterForMeal = useCallback((meal, preferredMode = 'meal_copy') => {
    const sourceMealId = meal?.id ? String(meal.id) : '';
    if (!sourceMealId) {
      openCopyCenter();
      return;
    }

    if (preferredMode === 'meal_copy') {
      setCopyMode('meal_copy');
      setCopySourceDate(currentDateKey);
      setCopySourceMealId(sourceMealId);

      const fallbackDestination =
        filteredMeals.find((item) => String(item.id) !== sourceMealId)
        || filteredMeals[0]
        || null;

      setCopyDestinationMealId(fallbackDestination ? String(fallbackDestination.id) : '');
      setIsCopyDrawerOpen(true);
      return;
    }

    // const defaultTargetDate = getOffsetDateKey(currentDate, 1);
    // const mealsOnTargetDate = getMealsForDate(defaultTargetDate);

    setCopyMode('meal_copy');
    setCopySourceDate(currentDateKey);
    setCopyTargetDate(currentDateKey);
    setCopySourceMealId(sourceMealId);
    setIsCopyDrawerOpen(true);
  }, [openCopyCenter, currentDateKey, filteredMeals, getOffsetDateKey, currentDate, getMealsForDate]);

  const currentMealsWithFoods = useMemo(
    () => filteredMeals.filter((meal) => (meal.foods || []).length > 0),
    [filteredMeals]
  );

  const findDateWithSourceMeals = useCallback((preferredDateKey = currentDateKey) => {
    if (preferredDateKey && getSourceMealsForDate(preferredDateKey).length > 0) {
      return preferredDateKey;
    }

    const mealDates = [];
    allMeals.forEach((meal) => {
      const foods = Array.isArray(meal.foods) ? meal.foods : [];
      foods.forEach((food) => {
        const dateKey = typeof food?.createdAt === 'string' ? food.createdAt.split('T')[0] : '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          mealDates.push(dateKey);
        }
      });
    });

    const uniqueDates = Array.from(new Set(mealDates))
      .filter((dateKey) => dateKey <= currentDateKey);
    uniqueDates.sort((a, b) => {
      const preferredTime = fromDateKey(preferredDateKey).getTime();
      const distanceA = Math.abs(fromDateKey(a).getTime() - preferredTime);
      const distanceB = Math.abs(fromDateKey(b).getTime() - preferredTime);

      if (distanceA !== distanceB) {
        return distanceA - distanceB;
      }

      return b.localeCompare(a);
    });

    return uniqueDates.find((dateKey) => getSourceMealsForDate(dateKey).length > 0) || preferredDateKey;
  }, [allMeals, currentDateKey, fromDateKey, getSourceMealsForDate]);

  const switchCopyMode = useCallback((nextMode) => {
    setCopyMode(nextMode);

    if (nextMode === 'meal_copy') {
      const sourceDate = findDateWithSourceMeals(currentDateKey);
      const sourceMeals = getSourceMealsForDate(sourceDate);
      const sourceMealId = sourceMeals[0]?.id ? String(sourceMeals[0].id) : '';
      const fallbackDestination =
        filteredMeals.find((meal) => String(meal.id) !== sourceMealId)
        || filteredMeals[0]
        || null;

      setCopySourceDate(sourceDate);
      setCopySourceMealId(sourceMealId);
      setCopyDestinationMealId(fallbackDestination ? String(fallbackDestination.id) : '');

      return;
    }

    if (nextMode === 'full_day_copy') {
      setCopySourceDate(currentDateKey);
      setCopyTargetDate('');
      setCopySourceMealId('');
      setCopyDestinationMealId('');
      return;
    }
  }, [currentDate, currentDateKey, filteredMeals, findDateWithSourceMeals, getSourceMealsForDate]);

  const targetCaloriesGoal = nutritionalGoals?.targetCalories;

  const mobileCalorieOverview = useMemo(() => {
    const foodCalories = filteredMeals.reduce((mealSum, meal) => (
      mealSum + (meal.foods || []).reduce((foodSum, food) => {
        const macronutrients = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
        const calories = Number(macronutrients['energy-kcal'] || macronutrients.calories || macronutrients.energy || 0);
        return foodSum + (Number.isFinite(calories) ? calories : 0);
      }, 0)
    ), 0);

    const parsedGoal = Number(targetCaloriesGoal);
    const hasConfiguredGoal = Number.isFinite(parsedGoal) && parsedGoal > 0;
    const goalCalories = hasConfiguredGoal ? parsedGoal : Math.max(foodCalories, 0);
    const remainingCalories = goalCalories - foodCalories;

    return {
      hasConfiguredGoal,
      goalCalories,
      foodCalories,
      remainingCalories,
    };
  }, [filteredMeals, calculateNutrition, targetCaloriesGoal]);

  const sourceMealsForSelectedDate = useMemo(
    () => (copySourceDate ? getSourceMealsForDate(copySourceDate) : []),
    [copySourceDate, getSourceMealsForDate]
  );

  const targetMealsForSelectedDate = useMemo(
    () => (copyTargetDate ? getMealsForDate(copyTargetDate) : []),
    [copyTargetDate, getMealsForDate]
  );

  const selectedSourceMealForCopy = useMemo(() => {
    if (copyMode === 'meal_copy') {
      return sourceMealsForSelectedDate.find((meal) => String(meal.id) === String(copySourceMealId)) || null;
    }
    if (copyMode === 'full_day_copy') {
      return currentMealsWithFoods.find((meal) => String(meal.id) === String(copySourceMealId)) || null;
    }
    return null;
  }, [copyMode, sourceMealsForSelectedDate, currentMealsWithFoods, copySourceMealId]);

  const selectedDestinationMealForCopy = useMemo(() => {
    if (copyMode === 'meal_copy') {
      return targetMealsForSelectedDate.find((meal) => String(meal.id) === String(copyDestinationMealId)) || null;
    }
    if (copyMode === 'full_day_copy') {
      return targetMealsForSelectedDate.find((meal) => String(meal.id) === String(copyDestinationMealId)) || null;
    }
    return null;
  }, [copyMode, targetMealsForSelectedDate, copyDestinationMealId]);

  const copySummary = useMemo(() => {
    if (copyMode === 'full_day_copy') {
      const foodsCount = sourceMealsForSelectedDate.reduce((sum, meal) => sum + (meal.foods || []).length, 0);

      return {
        title: 'Résumé',
        source: copySourceDate
          ? formatDateShort(copySourceDate)
          : 'Date source',
        destination: copyTargetDate
          ? formatDateShort(copyTargetDate)
          : 'Date destination',
        detail: `${foodsCount} aliment${foodsCount > 1 ? 's' : ''} à copier.`,
      };
    }

    if (copyMode === 'meal_copy') {
      const foodsCount = (selectedSourceMealForCopy?.foods || []).length;

      return {
        title: 'Résumé',
        source: selectedSourceMealForCopy
          ? `${selectedSourceMealForCopy.name || 'Repas source'} · ${copySourceDate ? formatDateShort(copySourceDate) : 'date source'}`
          : 'Repas source',
        destination: selectedDestinationMealForCopy
          ? `${selectedDestinationMealForCopy.name || 'Repas destination'} · ${copyTargetDate ? formatDateShort(copyTargetDate) : 'date destination'}`
          : (copyTargetDate ? formatDateShort(copyTargetDate) : 'Date destination'),
        detail: `${foodsCount} aliment${foodsCount > 1 ? 's' : ''} à copier.`,
      };
    }

    // fallback: treat as full_day_copy
    const foodsCount = sourceMealsForSelectedDate.reduce((sum, meal) => sum + (meal.foods || []).length, 0);

    return {
      title: 'Résumé',
      source: copySourceDate
        ? formatDateShort(copySourceDate)
        : 'Date source',
      destination: copyTargetDate
        ? formatDateShort(copyTargetDate)
        : 'Date destination',
      detail: `${foodsCount} aliment${foodsCount > 1 ? 's' : ''} à copier.`,
    };
  }, [
    copyMode,
    copySourceDate,
    copyTargetDate,
    currentDateKey,
    formatDateShort,
    selectedDestinationMealForCopy,
    selectedSourceMealForCopy,
    sourceMealsForSelectedDate
  ]);

  useEffect(() => {
    if (!isCopyDrawerOpen) {
      return;
    }

    if (copyMode === 'meal_copy') {
      if (!sourceMealsForSelectedDate.some((meal) => String(meal.id) === String(copySourceMealId))) {
        setCopySourceMealId(sourceMealsForSelectedDate[0]?.id ? String(sourceMealsForSelectedDate[0].id) : '');
      }

      if (!filteredMeals.some((meal) => String(meal.id) === String(copyDestinationMealId))) {
        setCopyDestinationMealId(filteredMeals[0]?.id ? String(filteredMeals[0].id) : '');
      }
      return;
    }

    if (copySourceMealId !== '') {
      setCopySourceMealId('');
    }
    if (copyDestinationMealId !== '') {
      setCopyDestinationMealId('');
    }
  }, [
    isCopyDrawerOpen,
    copyMode,
    sourceMealsForSelectedDate,
    targetMealsForSelectedDate,
    filteredMeals,
    currentMealsWithFoods,
    copySourceMealId,
    copyDestinationMealId
  ]);

  const copyMealContent = async (
    sourceMeal,
    destinationMeal,
    targetDate = null,
    options = {}
  ) => {
    const { refreshAfterCopy = true, showSuccessToast = true } = options;
    const sourceFoods = Array.isArray(sourceMeal?.foods) ? sourceMeal.foods : [];

    if (!sourceFoods.length) {
      if (showSuccessToast) {
        showToast('Le repas source ne contient aucun aliment');
      }
      return { total: 0, copied: 0, failed: 0 };
    }

    if (!destinationMeal?.id) {
      throw new Error('Repas de destination introuvable');
    }

    const destinationDateKey = targetDate || getDateKey();
    const sourceDateKey = sourceFoods[0]?.createdAt?.split('T')[0] || null;

    if (sourceMeal.id === destinationMeal.id && sourceDateKey === destinationDateKey) {
      if (showSuccessToast) {
        showToast('Ce repas est déjà sur cette date');
      }
      return { total: sourceFoods.length, copied: 0, failed: 0 };
    }

    const baseSortOrder = getNextFoodSortOrder(destinationMeal);

    const copyJobs = sourceFoods.map((food, foodIndex) =>
      journalService.addFoodItem(destinationMeal.id, {
        barcode: food.barcode,
        name: food.name,
        quantity: food.quantity,
        macronutrients: food.macronutrients,
        note: food.note || '',
        ingredients: food.ingredients || [],
        targetDate: destinationDateKey,
        sortOrder: baseSortOrder + foodIndex,
      })
    );

    const results = await Promise.allSettled(copyJobs);
    const copied = results.filter((result) => result.status === 'fulfilled').length;
    const failed = results.length - copied;

    if (refreshAfterCopy) {
      await loadJournalFromAPI();
    }

    if (showSuccessToast) {
      if (copied > 0 && failed === 0) {
        showToast(`${copied} aliment${copied > 1 ? 's' : ''} copié${copied > 1 ? 's' : ''} vers "${destinationMeal.name}"`);
      } else if (copied > 0) {
        showToast(`${copied} aliment${copied > 1 ? 's' : ''} copié${copied > 1 ? 's' : ''}, ${failed} échec${failed > 1 ? 's' : ''}`);
      } else {
        showToast('Erreur lors de la copie');
      }
    }

    return { total: results.length, copied, failed };
  };

  const copyFullDayMeals = async (sourceMeals, targetDateKey, successLabel) => {
    if (!journalId) {
      showToast('Journal indisponible. Réessayez dans quelques secondes.');
      return { success: false, copiedFoods: 0 };
    }

    if (!sourceMeals.length) {
      showToast('Aucun repas source avec aliments');
      return { success: false, copiedFoods: 0 };
    }

    setIsProcessing(true);

    try {
      const destinationMeals = getMealsForDate(targetDateKey);
      const destinationByName = new Map();

      destinationMeals.forEach((meal) => {
        const key = normalizeMealName(meal.name);
        if (key && !destinationByName.has(key)) {
          destinationByName.set(key, meal);
        }
      });

      let copiedMeals = 0;
      let copiedFoods = 0;
      let failedFoods = 0;
      let createdMeals = 0;

      for (const sourceMeal of sourceMeals) {
        const sourceKey = normalizeMealName(sourceMeal.name);
        let destinationMeal = sourceKey ? destinationByName.get(sourceKey) : null;

        if (!destinationMeal) {
          destinationMeal = await journalService.addMeal(journalId, {
            name: sourceMeal.name || 'Repas copié',
            mealTime: sourceMeal.mealTime || null,
          });
          const destinationKey = normalizeMealName(destinationMeal.name);
          if (destinationKey) {
            destinationByName.set(destinationKey, destinationMeal);
          }
          createdMeals += 1;
        }

        const result = await copyMealContent(
          sourceMeal,
          destinationMeal,
          targetDateKey,
          { refreshAfterCopy: false, showSuccessToast: false }
        );

        if (result.copied > 0) {
          copiedMeals += 1;
        }
        copiedFoods += result.copied;
        failedFoods += result.failed;
      }

      await loadJournalFromAPI();

      if (copiedFoods === 0) {
        showToast('Aucun aliment copié');
        return { success: false, copiedFoods: 0 };
      }

      const createdPart = createdMeals > 0
        ? `, ${createdMeals} repas créé${createdMeals > 1 ? 's' : ''}`
        : '';
      const failedPart = failedFoods > 0
        ? ` (${failedFoods} échec${failedFoods > 1 ? 's' : ''})`
        : '';
      showToast(`${successLabel}: ${copiedFoods} aliment${copiedFoods > 1 ? 's' : ''} dans ${copiedMeals} repas${createdPart}${failedPart}`);
      return { success: true, copiedFoods };
    } catch (error) {
      console.error('Erreur copie journée:', error);
      showToast('Erreur lors de la copie de la journée');
      return { success: false, copiedFoods: 0 };
    } finally {
      setIsProcessing(false);
    }
  };

  const getMealDisplayLabel = useCallback((meal) => {
    const foodCount = (meal.foods || []).length;
    return `${meal.name || 'Repas sans nom'} • ${foodCount} aliment${foodCount > 1 ? 's' : ''}`;
  }, []);


  const handleCopyCenterSubmit = async () => {
    if (isProcessing) return;

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      showToast('Copie indisponible hors ligne. Reconnectez-vous pour continuer.');
      return;
    }

    setIsProcessing(true);

    try {
      switch (copyMode) {
        case 'full_day_copy':
          await handleFullDayCopy();
          break;

        case 'meal_copy':
          await handleMealCopy();
          break;

        default:
          await handleSingleMealCopy();
          break;
      }
    } catch (error) {
      console.error('Erreur copie:', error);
      showToast('Erreur lors de la copie');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFullDayCopy = async () => {
    if (!copySourceDate) {
      showToast('Sélectionnez une date source.');
      return;
    }

    if (copySourceDate === currentDateKey) {
      showToast('Choisissez une date source différente de la date affichée.');
      return;
    }

    const sourceMeals = getSourceMealsForDate(copySourceDate);

    if (!sourceMeals.length) {
      showToast(`Aucun repas avec aliments le ${formatDateShort(copySourceDate)}.`);
      return;
    }

    const result = await copyFullDayMeals(
      sourceMeals,
      copyTargetDate,
      `${formatDateShort(copySourceDate)} → ${formatDateShort(copyTargetDate)}`
    );

    if (result?.success) {
      setIsCopyDrawerOpen(false);
    }
  };

  const handleMealCopy = async () => {
    if (filteredMeals.length === 0) {
      showToast("Créez d'abord un repas pour la date affichée.");
      return;
    }

    const sourceMeal = getMealById(sourceMealsForSelectedDate, copySourceMealId);
    if (!sourceMeal) {
      showToast('Sélectionnez un repas source valide.');
      return;
    }

    const destinationMeal = getMealById(targetMealsForSelectedDate, copyDestinationMealId);

    if (!destinationMeal) {
      showToast('Sélectionnez un repas destination valide.');
      return;
    }

    const result = await copyMealContent(
      sourceMeal,
      destinationMeal,
      copyTargetDate
    );

    if (result?.copied > 0) {
      setIsCopyDrawerOpen(false);
    }
  };

  const handleSingleMealCopy = async () => {
    if (!copyTargetDate) {
      showToast('Sélectionnez une date destination.');
      return;
    }

    if (copyTargetDate === copySourceDate) {
      showToast('Choisissez une date destination différente de la date source.');
      return;
    }

    const sourceMeal = getMealById(currentMealsWithFoods, copySourceMealId);
    if (!sourceMeal) {
      showToast('Sélectionnez un repas source valide.');
      return;
    }

    const destinationMeal = getMealById(targetMealsForSelectedDate, copyDestinationMealId);

    if (!destinationMeal) {
      if (!targetMealsForSelectedDate.length) {
        showToast(`Aucun repas disponible le ${formatDateShort(copyTargetDate)}.`);
      } else {
        showToast('Sélectionnez un repas destination valide.');
      }
      return;
    }

    const result = await copyMealContent(sourceMeal, destinationMeal, copyTargetDate);

    if (result?.copied > 0) {
      setIsCopyDrawerOpen(false);
    }
  };

  const calculateNutritionForDisplay = (nutrition100g, quantity) => {
    return buildAdjustedNutritionForDisplay(nutrition100g || {}, quantity);
  };

  const getNextFoodSortOrder = useCallback((meal) => {
    const foods = Array.isArray(meal?.foods) ? meal.foods : [];

    const maxSortOrder = foods.reduce((max, food, index) => {
      const currentSort = Number.isFinite(Number(food?.sortOrder)) ? Number(food.sortOrder) : index;
      return Math.max(max, currentSort);
    }, -1);

    return maxSortOrder + 1;
  }, []);

  const getFoodSelectionKey = useCallback((mealId, food) => {
    if (food?.id !== undefined && food?.id !== null) {
      return `id:${food.id}`;
    }

    const createdAt = String(food?.createdAt || '');
    const name = String(food?.name || '').trim().toLowerCase();
    const quantity = String(food?.quantity ?? '');
    return `tmp:${mealId}:${createdAt}:${name}:${quantity}`;
  }, []);

  const toggleFoodSelection = useCallback((mealId, foodSelectionKey) => {
    const normalizedMealId = String(mealId);
    setSelectedFoodsByMeal((prev) => {
      const currentSelection = prev[normalizedMealId] || [];
      const isSelected = currentSelection.includes(foodSelectionKey);
      const nextSelection = isSelected
        ? currentSelection.filter((item) => item !== foodSelectionKey)
        : [...currentSelection, foodSelectionKey];

      const next = { ...prev };
      if (nextSelection.length > 0) {
        next[normalizedMealId] = nextSelection;
      } else {
        delete next[normalizedMealId];
      }
      return next;
    });
  }, []);

  const clearFoodSelectionForMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    setSelectedFoodsByMeal((prev) => {
      if (!prev[normalizedMealId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[normalizedMealId];
      return next;
    });
  }, []);

  const selectAllFoodsInMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    const meal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!meal) {
      return;
    }

    const allSelectionKeys = (meal.foods || []).map((food) => getFoodSelectionKey(normalizedMealId, food));
    setSelectedFoodsByMeal((prev) => {
      if (allSelectionKeys.length === 0) {
        return prev;
      }
      return {
        ...prev,
        [normalizedMealId]: allSelectionKeys,
      };
    });
  }, [filteredMeals, getFoodSelectionKey]);

  const setBulkMoveTargetForMeal = useCallback((mealId, destinationMealId) => {
    const normalizedMealId = String(mealId);
    const normalizedDestination = destinationMealId ? String(destinationMealId) : '';
    setBulkMoveTargetByMeal((prev) => ({
      ...prev,
      [normalizedMealId]: normalizedDestination,
    }));
  }, []);

  useEffect(() => {
    setSelectedFoodsByMeal((prev) => {
      let changed = false;
      const next = {};

      filteredMeals.forEach((meal) => {
        const mealId = String(meal.id);
        const currentSelection = prev[mealId];
        if (!currentSelection || currentSelection.length === 0) {
          return;
        }

        const validKeys = new Set((meal.foods || []).map((food) => getFoodSelectionKey(mealId, food)));
        const filteredSelection = currentSelection.filter((key) => validKeys.has(key));

        if (filteredSelection.length > 0) {
          next[mealId] = filteredSelection;
        }

        if (filteredSelection.length !== currentSelection.length) {
          changed = true;
        }
      });

      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [filteredMeals, getFoodSelectionKey]);

  useEffect(() => {
    setBulkMoveTargetByMeal((prev) => {
      let changed = false;
      const next = {};

      filteredMeals.forEach((meal) => {
        const mealId = String(meal.id);
        const availableDestinationMeals = filteredMeals.filter(
          (candidateMeal) => String(candidateMeal.id) !== mealId && !isPendingOfflineMeal(candidateMeal)
        );
        const currentTarget = prev[mealId] ? String(prev[mealId]) : '';

        if (availableDestinationMeals.length === 0) {
          if (currentTarget) {
            changed = true;
          }
          return;
        }

        const hasCurrentTarget = currentTarget
          && availableDestinationMeals.some((candidateMeal) => String(candidateMeal.id) === currentTarget);
        const resolvedTarget = hasCurrentTarget
          ? currentTarget
          : String(availableDestinationMeals[0].id);

        if (resolvedTarget !== currentTarget) {
          changed = true;
        }

        next[mealId] = resolvedTarget;
      });

      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [filteredMeals, isPendingOfflineMeal]);

  const reorderMeals = useCallback(async (fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex) {
      return false;
    }

    if (fromIndex < 0 || toIndex < 0 || fromIndex >= filteredMeals.length || toIndex >= filteredMeals.length) {
      return false;
    }

    const newFilteredMeals = [...filteredMeals];
    const [movedMeal] = newFilteredMeals.splice(fromIndex, 1);
    if (!movedMeal) {
      return false;
    }
    newFilteredMeals.splice(toIndex, 0, movedMeal);

    setFilteredMeals(newFilteredMeals);

    try {
      const mealsWithNewOrder = newFilteredMeals.map((meal, index) => ({
        ...meal,
        sortOrder: index,
      }));

      const result = await journalService.updateMealsOrder(mealsWithNewOrder, journalId);

      setAllMeals((prevMeals) => {
        const updatedMeals = [...prevMeals];
        mealsWithNewOrder.forEach((meal) => {
          const mealIndex = updatedMeals.findIndex((m) => m.id === meal.id);
          if (mealIndex !== -1) {
            updatedMeals[mealIndex] = { ...updatedMeals[mealIndex], sortOrder: meal.sortOrder };
          }
        });
        return updatedMeals;
      });

      showToast(result?.queued ? 'Ordre des repas enregistré (synchronisation en attente).' : 'Ordre des repas sauvegardé');
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde ordre:', error);
      showToast('Erreur lors de la sauvegarde de l\'ordre');
      await loadJournalFromAPI();
      return false;
    }
  }, [filteredMeals, journalId, loadJournalFromAPI, showToast]);

  const moveMealByOffset = useCallback(async (mealIndex, offset) => {
    const targetIndex = mealIndex + offset;
    if (targetIndex < 0 || targetIndex >= filteredMeals.length) {
      return;
    }
    await reorderMeals(mealIndex, targetIndex);
  }, [filteredMeals.length, reorderMeals]);

  const handleDragStart = (e, index) => {
    if (e.target?.closest?.('button, input, select, textarea, a')) {
      e.preventDefault();
      return;
    }

    setDraggedItem(index);
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e) => {
    e?.currentTarget?.classList?.remove('dragging');
    setDraggedItem(null);
    setOverIndex(null);
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === undefined || draggedItem === targetIndex) return;

    await reorderMeals(draggedItem, targetIndex);

    setDraggedItem(null);
    setOverIndex(null);
  };

  const handleFoodDragStart = (e, mealId, foodIndex) => {
    if (e.target?.closest?.('button, input, select, textarea, a')) {
      e.preventDefault();
      return;
    }

    e.stopPropagation();
    setDraggedFood({ mealId: String(mealId), foodIndex });
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFoodDragEnd = (e) => {
    e?.stopPropagation();
    e?.currentTarget?.classList?.remove('dragging');
    setDraggedFood(null);
    setFoodOverTarget(null);
  };

  const moveFoodToMealViaDrop = useCallback(async (sourceMealId, sourceFoodIndex, destinationMealId) => {
    const normalizedSourceMealId = String(sourceMealId);
    const normalizedDestinationMealId = String(destinationMealId);

    const sourceMeal = filteredMeals.find((item) => String(item.id) === normalizedSourceMealId);
    const destinationMeal = filteredMeals.find((item) => String(item.id) === normalizedDestinationMealId);

    if (!sourceMeal || !destinationMeal) {
      return false;
    }

    if (isPendingOfflineMeal(sourceMeal) || isPendingOfflineMeal(destinationMeal)) {
      showToast('Repas hors ligne : déplacement indisponible.');
      return false;
    }

    const food = (sourceMeal.foods || [])[sourceFoodIndex];
    if (!food?.id) {
      return false;
    }

    try {
      await journalService.updateFoodItem(food.id, {
        meal: `/api/meals/${normalizedDestinationMealId}`,
        sortOrder: getNextFoodSortOrder(destinationMeal),
      });

      await loadJournalFromAPI();
      showToast(`Aliment déplacé vers "${destinationMeal.name || 'repas'}"`);
      return true;
    } catch (error) {
      console.error('Erreur déplacement aliment (drag & drop) vers un autre repas:', error);
      showToast('Erreur lors du déplacement de l\'aliment');
      return false;
    } finally {
      setDraggedFood(null);
      setFoodOverTarget(null);
    }
  }, [filteredMeals, isPendingOfflineMeal, getNextFoodSortOrder, loadJournalFromAPI, showToast]);

  const persistFoodsOrderForMeal = useCallback(async (mealId, orderedFoods, successMessage = 'Ordre des aliments sauvegardé') => {
    const normalizedMealId = String(mealId);
    const foodsWithSortOrder = (orderedFoods || []).map((food, index) => ({
      ...food,
      sortOrder: index,
    }));

    setFilteredMeals((prevMeals) => prevMeals.map((item) => (
      String(item.id) === normalizedMealId
        ? { ...item, foods: foodsWithSortOrder }
        : item
    )));

    setAllMeals((prevMeals) => prevMeals.map((item) => (
      String(item.id) === normalizedMealId
        ? { ...item, foods: foodsWithSortOrder }
        : item
    )));

    try {
      await journalService.updateFoodItemsOrder(foodsWithSortOrder);
      showToast(successMessage);
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde ordre aliments:', error);
      showToast('Erreur lors de la sauvegarde de l’ordre des aliments');
      await loadJournalFromAPI();
      return false;
    } finally {
      setDraggedFood(null);
      setFoodOverTarget(null);
    }
  }, [loadJournalFromAPI, showToast]);

  const reorderFoodsInMeal = useCallback(async (mealId, fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex) {
      return false;
    }

    const normalizedMealId = String(mealId);
    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!targetMeal) {
      return false;
    }

    const currentFoods = [...(targetMeal.foods || [])];
    if (
      fromIndex < 0
      || toIndex < 0
      || fromIndex >= currentFoods.length
      || toIndex >= currentFoods.length
    ) {
      return false;
    }

    const [movedFood] = currentFoods.splice(fromIndex, 1);
    if (!movedFood) {
      return false;
    }

    currentFoods.splice(toIndex, 0, movedFood);
    return persistFoodsOrderForMeal(mealId, currentFoods);
  }, [filteredMeals, persistFoodsOrderForMeal]);

  const handleFoodDrop = async (e, mealId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedFood) {
      return;
    }

    const destinationMealId = String(mealId);

    if (draggedFood.mealId === destinationMealId) {
      if (draggedFood.foodIndex === targetIndex) return;
      await reorderFoodsInMeal(mealId, draggedFood.foodIndex, targetIndex);
      return;
    }

    await moveFoodToMealViaDrop(draggedFood.mealId, draggedFood.foodIndex, destinationMealId);
  };

  const resetTouchDragState = useCallback(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('touch-drag-active');
    }
    if (touchDragGhostFrameRef.current) {
      window.cancelAnimationFrame(touchDragGhostFrameRef.current);
      touchDragGhostFrameRef.current = null;
    }
    removeTouchDragPreview();

    touchDragStateRef.current = {
      active: false,
      type: null,
      pointerId: null,
      sourceMealIndex: null,
      targetMealIndex: null,
      sourceMealId: null,
      sourceFoodIndex: null,
      targetFoodIndex: null,
    };
    setDraggedItem(null);
    setOverIndex(null);
    setDraggedFood(null);
    setFoodOverTarget(null);
  }, [removeTouchDragPreview]);

  const startTouchMealDrag = useCallback((event, mealIndex, dragEnabled, sourceElement) => {
    if (!dragEnabled) return;
    if (event.pointerType !== 'touch') return;

    event.preventDefault();
    if (typeof document !== 'undefined') {
      document.body.classList.add('touch-drag-active');
    }

    touchDragStateRef.current = {
      active: true,
      type: 'meal',
      pointerId: event.pointerId,
      sourceMealIndex: mealIndex,
      targetMealIndex: mealIndex,
      sourceMealId: null,
      sourceFoodIndex: null,
      targetFoodIndex: null,
    };

    setDraggedItem(mealIndex);
    setOverIndex(mealIndex);
    createTouchDragPreviewFromElement(sourceElement, event.clientX, event.clientY);
  }, [createTouchDragPreviewFromElement]);

  const startTouchFoodDrag = useCallback((event, mealId, foodIndex, dragEnabled, sourceElement) => {
    if (!dragEnabled) return;
    if (event.pointerType !== 'touch') return;

    event.preventDefault();
    if (typeof document !== 'undefined') {
      document.body.classList.add('touch-drag-active');
    }

    const normalizedMealId = String(mealId);
    touchDragStateRef.current = {
      active: true,
      type: 'food',
      pointerId: event.pointerId,
      sourceMealIndex: null,
      targetMealIndex: null,
      sourceMealId: normalizedMealId,
      sourceFoodIndex: foodIndex,
      targetFoodIndex: foodIndex,
    };

    setDraggedFood({ mealId: normalizedMealId, foodIndex });
    setFoodOverTarget({ mealId: normalizedMealId, foodIndex });
    createTouchDragPreviewFromElement(sourceElement, event.clientX, event.clientY);
  }, [createTouchDragPreviewFromElement]);

  useEffect(() => {
    const findNearestByY = (elements, clientY) => {
      if (!Array.isArray(elements) || elements.length === 0) {
        return null;
      }

      let nearestElement = null;
      let minDistance = Number.POSITIVE_INFINITY;

      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const centerY = rect.top + (rect.height / 2);
        const distance = Math.abs(clientY - centerY);
        if (distance < minDistance) {
          minDistance = distance;
          nearestElement = element;
        }
      });

      return nearestElement;
    };

    const handlePointerMove = (event) => {
      const state = touchDragStateRef.current;
      if (!state.active || event.pointerType !== 'touch') {
        return;
      }
      if (Number.isInteger(state.pointerId) && event.pointerId !== state.pointerId) {
        return;
      }

      event.preventDefault();
      scheduleTouchGhostUpdate(event.clientX + 14, event.clientY + 14);

      const targetElement = document.elementFromPoint(event.clientX, event.clientY);
      if (!targetElement) {
        return;
      }

      if (state.type === 'meal') {
        let mealElement = targetElement.closest('[data-meal-dnd-index]');
        if (!mealElement) {
          const mealCandidates = Array.from(document.querySelectorAll('[data-meal-dnd-index]'));
          mealElement = findNearestByY(mealCandidates, event.clientY);
        }
        if (!mealElement) {
          return;
        }

        const nextIndex = Number.parseInt(mealElement.getAttribute('data-meal-dnd-index') || '', 10);
        if (!Number.isInteger(nextIndex)) {
          return;
        }

        if (state.targetMealIndex !== nextIndex) {
          touchDragStateRef.current.targetMealIndex = nextIndex;
          setOverIndex(nextIndex);
        }
        return;
      }

      if (state.type === 'food') {
        let foodElement = targetElement.closest('[data-food-dnd-meal-id][data-food-dnd-index]');
        if (!foodElement) {
          const foodCandidates = Array.from(document.querySelectorAll('[data-food-dnd-meal-id][data-food-dnd-index]'))
            .filter((element) => String(element.getAttribute('data-food-dnd-meal-id') || '') === String(state.sourceMealId));
          foodElement = findNearestByY(foodCandidates, event.clientY);
        }
        if (!foodElement) {
          return;
        }

        const mealId = String(foodElement.getAttribute('data-food-dnd-meal-id') || '');
        const nextIndex = Number.parseInt(foodElement.getAttribute('data-food-dnd-index') || '', 10);
        if (!mealId || !Number.isInteger(nextIndex)) {
          return;
        }

        if (mealId !== String(state.sourceMealId)) {
          return;
        }

        if (state.targetFoodIndex !== nextIndex) {
          touchDragStateRef.current.targetFoodIndex = nextIndex;
          setFoodOverTarget({ mealId, foodIndex: nextIndex });
        }
      }
    };

    const handlePointerEnd = async (event) => {
      const state = touchDragStateRef.current;
      if (!state.active) {
        return;
      }

      if (event.pointerType && event.pointerType !== 'touch') {
        return;
      }
      if (Number.isInteger(state.pointerId) && event.pointerId !== state.pointerId) {
        return;
      }

      if (state.type === 'meal') {
        const fromIndex = state.sourceMealIndex;
        const toIndex = state.targetMealIndex;
        resetTouchDragState();

        if (Number.isInteger(fromIndex) && Number.isInteger(toIndex) && fromIndex !== toIndex) {
          await reorderMeals(fromIndex, toIndex);
        }
        return;
      }

      if (state.type === 'food') {
        const mealId = String(state.sourceMealId || '');
        const fromIndex = state.sourceFoodIndex;
        const toIndex = state.targetFoodIndex;
        resetTouchDragState();

        if (mealId && Number.isInteger(fromIndex) && Number.isInteger(toIndex) && fromIndex !== toIndex) {
          await reorderFoodsInMeal(mealId, fromIndex, toIndex);
        }
      }
    };

    const handlePointerCancel = () => {
      if (touchDragStateRef.current.active) {
        resetTouchDragState();
      }
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('touch-drag-active');
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [reorderMeals, reorderFoodsInMeal, resetTouchDragState, scheduleTouchGhostUpdate]);

  const moveFoodAcrossMeals = useCallback(async (sourceMealId, foodIndex, direction) => {
    const normalizedMealId = String(sourceMealId);
    const mealIdx = filteredMeals.findIndex((item) => String(item.id) === normalizedMealId);
    if (mealIdx === -1) return false;

    const destinationMealIdx = direction === 'down' ? mealIdx + 1 : mealIdx - 1;
    if (destinationMealIdx < 0 || destinationMealIdx >= filteredMeals.length) {
      return false;
    }

    const sourceMeal = filteredMeals[mealIdx];
    const destinationMeal = filteredMeals[destinationMealIdx];

    if (isPendingOfflineMeal(sourceMeal) || isPendingOfflineMeal(destinationMeal)) {
      showToast('Repas hors ligne : déplacement indisponible.');
      return false;
    }

    const food = (sourceMeal.foods || [])[foodIndex];
    if (!food?.id) {
      return false;
    }

    try {
      await journalService.updateFoodItem(food.id, {
        meal: `/api/meals/${destinationMeal.id}`,
        sortOrder: getNextFoodSortOrder(destinationMeal),
      });

      await loadJournalFromAPI();
      showToast(`Aliment déplacé vers "${destinationMeal.name || 'repas'}"`);
      return true;
    } catch (error) {
      console.error('Erreur déplacement aliment vers un autre repas:', error);
      showToast('Erreur lors du déplacement de l\'aliment');
      return false;
    }
  }, [filteredMeals, isPendingOfflineMeal, getNextFoodSortOrder, loadJournalFromAPI, showToast]);

  const moveFoodByOffset = useCallback(async (mealId, foodIndex, offset) => {
    const normalizedMealId = String(mealId);
    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    const foodsCount = (targetMeal?.foods || []).length;
    const targetIndex = foodIndex + offset;

    if (targetIndex >= 0 && targetIndex < foodsCount) {
      await reorderFoodsInMeal(mealId, foodIndex, targetIndex);
      return;
    }

    await moveFoodAcrossMeals(mealId, foodIndex, offset > 0 ? 'down' : 'up');
  }, [filteredMeals, reorderFoodsInMeal, moveFoodAcrossMeals]);

  const moveSelectedFoodsInMeal = useCallback(async (mealId, direction) => {
    const normalizedMealId = String(mealId);
    const selectedKeys = selectedFoodsByMeal[normalizedMealId] || [];
    if (selectedKeys.length === 0) {
      return false;
    }

    const targetMeal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!targetMeal) {
      return false;
    }

    const currentFoods = [...(targetMeal.foods || [])];
    if (currentFoods.length < 2) {
      return false;
    }

    const selectedSet = new Set(selectedKeys);
    const isSelectedFood = (food) => selectedSet.has(getFoodSelectionKey(normalizedMealId, food));

    if (!currentFoods.some(isSelectedFood)) {
      clearFoodSelectionForMeal(normalizedMealId);
      return false;
    }

    let nextFoods = [...currentFoods];

    if (direction === 'top') {
      const selectedFoods = currentFoods.filter(isSelectedFood);
      const unselectedFoods = currentFoods.filter((food) => !isSelectedFood(food));
      nextFoods = [...selectedFoods, ...unselectedFoods];
    } else if (direction === 'bottom') {
      const selectedFoods = currentFoods.filter(isSelectedFood);
      const unselectedFoods = currentFoods.filter((food) => !isSelectedFood(food));
      nextFoods = [...unselectedFoods, ...selectedFoods];
    } else if (direction === 'up') {
      for (let index = 1; index < nextFoods.length; index += 1) {
        if (isSelectedFood(nextFoods[index]) && !isSelectedFood(nextFoods[index - 1])) {
          const temp = nextFoods[index - 1];
          nextFoods[index - 1] = nextFoods[index];
          nextFoods[index] = temp;
        }
      }
    } else if (direction === 'down') {
      for (let index = nextFoods.length - 2; index >= 0; index -= 1) {
        if (isSelectedFood(nextFoods[index]) && !isSelectedFood(nextFoods[index + 1])) {
          const temp = nextFoods[index + 1];
          nextFoods[index + 1] = nextFoods[index];
          nextFoods[index] = temp;
        }
      }
    } else {
      return false;
    }

    const currentOrder = currentFoods.map((food) => getFoodSelectionKey(normalizedMealId, food)).join('||');
    const nextOrder = nextFoods.map((food) => getFoodSelectionKey(normalizedMealId, food)).join('||');

    if (currentOrder === nextOrder) {
      return false;
    }

    const selectedCount = nextFoods.filter(isSelectedFood).length;
    const successMessage = selectedCount > 1
      ? `${selectedCount} aliments réorganisés`
      : 'Aliment réorganisé';

    return persistFoodsOrderForMeal(mealId, nextFoods, successMessage);
  }, [clearFoodSelectionForMeal, filteredMeals, getFoodSelectionKey, persistFoodsOrderForMeal, selectedFoodsByMeal]);

  const getSelectedFoodsForMeal = useCallback((mealId) => {
    const normalizedMealId = String(mealId);
    const meal = filteredMeals.find((item) => String(item.id) === normalizedMealId);
    if (!meal) {
      return { meal: null, selectedFoods: [] };
    }

    const selectedKeys = selectedFoodsByMeal[normalizedMealId] || [];
    if (selectedKeys.length === 0) {
      return { meal, selectedFoods: [] };
    }

    const selectedKeySet = new Set(selectedKeys);
    const selectedFoods = (meal.foods || []).filter((food) => (
      selectedKeySet.has(getFoodSelectionKey(normalizedMealId, food))
    ));

    return { meal, selectedFoods };
  }, [filteredMeals, getFoodSelectionKey, selectedFoodsByMeal]);

  const deleteSelectedFoodsInMeal = useCallback((mealId) => {
    const { meal, selectedFoods } = getSelectedFoodsForMeal(mealId);
    if (!meal || selectedFoods.length === 0) {
      return;
    }

    const deletableFoods = selectedFoods.filter((food) => food?.id);
    if (deletableFoods.length === 0) {
      showToast('Aucun aliment sélectionné ne peut être supprimé.');
      return;
    }

    const count = deletableFoods.length;
    setModalConfig({
      title: count > 1 ? `Supprimer ${count} aliments ?` : 'Supprimer cet aliment ?',
      message: count > 1
        ? 'Les aliments sélectionnés seront supprimés de ce repas.'
        : `L'aliment "${deletableFoods[0]?.name || 'sélectionné'}" sera supprimé de ce repas.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmVariant: 'danger',
      size: 'sm',
      onConfirm: async () => {
        try {
          await Promise.all(deletableFoods.map((food) => journalService.deleteFoodItem(food.id)));
          clearFoodSelectionForMeal(mealId);
          closeModal();
          await loadJournalFromAPI();
          showToast(count > 1 ? `${count} aliments supprimés` : 'Aliment supprimé');
        } catch (error) {
          console.error('Erreur suppression multiple aliments:', error);
          closeModal();
          showToast('Erreur lors de la suppression des aliments');
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  }, [clearFoodSelectionForMeal, closeModal, getSelectedFoodsForMeal, loadJournalFromAPI, showToast]);

  const moveSelectedFoodsToMeal = useCallback(async (sourceMealId, destinationMealId) => {
    const sourceId = String(sourceMealId);
    const destinationId = String(destinationMealId || '').trim();
    if (!destinationId) {
      showToast('Sélectionnez un repas destination.');
      return false;
    }

    if (sourceId === destinationId) {
      showToast('Choisissez un autre repas pour le déplacement.');
      return false;
    }

    const { meal: sourceMeal, selectedFoods } = getSelectedFoodsForMeal(sourceId);
    if (!sourceMeal || selectedFoods.length === 0) {
      showToast('Aucun aliment sélectionné à déplacer.');
      return false;
    }

    const destinationMeal = filteredMeals.find((item) => String(item.id) === destinationId);
    if (!destinationMeal) {
      showToast('Repas destination introuvable.');
      return false;
    }

    if (isPendingOfflineMeal(destinationMeal)) {
      showToast('Le repas destination est hors ligne, déplacement indisponible.');
      return false;
    }

    const movableFoods = selectedFoods.filter((food) => Boolean(food?.id));
    if (movableFoods.length === 0) {
      showToast('Les aliments sélectionnés ne peuvent pas être déplacés.');
      return false;
    }

    let nextSortOrder = getNextFoodSortOrder(destinationMeal);

    try {
      for (const food of movableFoods) {
        await journalService.updateFoodItem(food.id, {
          meal: `/api/meals/${destinationId}`,
          sortOrder: nextSortOrder,
        });
        nextSortOrder += 1;
      }

      clearFoodSelectionForMeal(sourceId);
      await loadJournalFromAPI();

      const skippedCount = selectedFoods.length - movableFoods.length;
      const destinationLabel = destinationMeal.name || 'le repas destination';
      const baseMessage = movableFoods.length > 1
        ? `${movableFoods.length} aliments déplacés vers ${destinationLabel}`
        : `Aliment déplacé vers ${destinationLabel}`;

      showToast(skippedCount > 0 ? `${baseMessage} (${skippedCount} ignoré(s))` : baseMessage);
      return true;
    } catch (error) {
      console.error('Erreur déplacement multiple aliments:', error);
      showToast('Erreur lors du déplacement des aliments');
      return false;
    }
  }, [clearFoodSelectionForMeal, filteredMeals, getNextFoodSortOrder, getSelectedFoodsForMeal, isPendingOfflineMeal, loadJournalFromAPI, showToast]);

  const Toast = () => {
    if (!showToastMsg) return null;

    return (
      <Alert
        type="info"
        message={showToastMsg}
        duration={3000}
        onClose={() => setShowToastMsg(null)}
      />
    );
  };

  const renderMealElement = (meal, index) => {
    const mealTimeValue = String(meal.mealTime || '').slice(0, 5);
    const isEditingMealTime = editingMealTimeId === meal.id;
    const mealTimeDraft = mealTimeDrafts[meal.id] || '';
    let totals = {};
    totals['proteins'] = 0;
    totals['carbohydrates'] = 0;
    totals['fat'] = 0;
    totals['energy'] = 0;
    totals['sodium'] = 0;
    totals['sugars'] = 0;

    (meal.foods || []).forEach(food => {
      const actualNutrition = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
      totals['proteins'] += actualNutrition['proteins'] || 0;
      totals['carbohydrates'] += actualNutrition['carbohydrates'] || 0;
      totals['fat'] += actualNutrition['fat'] || 0;
      totals['energy'] += actualNutrition['energy'] || 0;
      totals['sodium'] += actualNutrition['sodium'] || 0;
      totals['sugars'] += actualNutrition['sugars'] || 0;
    });

    const mealCalories = Math.round(totals.energy);
    const mealIsPendingSync = isPendingOfflineMeal(meal);
    const isEditingName = editingMealId === meal.id;
    const mealNameValue = isEditingName
      ? (mealNameDrafts[meal.id] ?? meal.name ?? '')
      : (meal.name ?? '');
    const isMealActionsOpen = isMobileViewport && (openMealActionsId === meal.id || isEditingName);

    const renderMealTimeControl = () => {
      if (isEditingMealTime && !isReadOnly) {
        return (
          <div className="meal-time-editor" onClick={(event) => event.stopPropagation()}>
            <input
              type="time"
              className="meal-time-input"
              value={mealTimeDraft}
              onChange={(event) => setMealTimeDrafts((prev) => ({
                ...prev,
                [meal.id]: event.target.value,
              }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  saveMealTime(meal);
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelEditingMealTime(meal.id);
                }
              }}
              aria-label={`Heure du repas ${meal.name || ''}`.trim()}
            />
            <button
              type="button"
              className="meal-time-save"
              onClick={() => saveMealTime(meal)}
              disabled={savingMealTimeId === meal.id}
              aria-label="Enregistrer l’heure du repas"
              title="Enregistrer l’heure"
            >
              <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="meal-time-cancel"
              onClick={() => cancelEditingMealTime(meal.id)}
              disabled={savingMealTimeId === meal.id}
              aria-label="Annuler la modification de l’heure"
              title="Annuler"
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
            </button>
          </div>
        );
      }

      if (isReadOnly) {
        return (
          <time
            className="meal-time"
            dateTime={mealTimeValue || undefined}
            aria-label={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
            title={mealTimeValue ? `Heure du repas : ${mealTimeValue}` : 'Heure du repas non renseignée'}
          >
            <FontAwesomeIcon icon={faClock} aria-hidden="true" />
            {mealTimeValue || '—'}
          </time>
        );
      }

      return (
        <button
          type="button"
          className="meal-time"
          onClick={(event) => {
            event.stopPropagation();
            startEditingMealTime(meal);
          }}
          aria-label={mealTimeValue ? `Modifier l’heure du repas, ${mealTimeValue}` : 'Définir l’heure du repas'}
          title="Modifier l’heure du repas"
        >
          <FontAwesomeIcon icon={faClock} aria-hidden="true" />
          {mealTimeValue || 'Définir l’heure'}
          <FontAwesomeIcon icon={faPen} className="meal-time-edit-icon" aria-hidden="true" />
        </button>
      );
    };

    const toggleFoodIngredients = (foodKey) => {
      setCollapsedFoodIngredients((prev) => {
        const currentlyCollapsed = prev[foodKey] !== false;
        return {
          ...prev,
          [foodKey]: !currentlyCollapsed,
        };
      });
    };

    const formatGramCell = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return '0';
      }
      if (Math.abs(numeric) >= 10) {
        return `${Math.round(numeric)}`;
      }
      return numeric.toFixed(1).replace(/\.0$/, '');
    };

    const formatSodiumMgCell = (valueInGrams) => {
      const numeric = Number(valueInGrams);
      if (!Number.isFinite(numeric)) {
        return '0';
      }
      const milligrams = numeric * 1000;
      if (Math.abs(milligrams) >= 10) {
        return `${Math.round(milligrams)}`;
      }
      return milligrams.toFixed(1).replace(/\.0$/, '');
    };

    const useDesktopLayoutOnMobile = false;
    const canDragOnCurrentViewport = !isMobileViewport;
    const mealDragEnabled = canDragOnCurrentViewport && !mealIsPendingSync;
    const nativeHtmlDragEnabled = mealDragEnabled && !isCoarsePointerMode;
    const selectedFoodKeysForMeal = selectedFoodsByMeal[String(meal.id)] || [];
    const selectedFoodsCountForMeal = selectedFoodKeysForMeal.length;
    const hasSelectedFoodsInMeal = selectedFoodsCountForMeal > 0;
    const destinationMealsForSelection = filteredMeals.filter(
      (candidateMeal) => String(candidateMeal.id) !== String(meal.id) && !isPendingOfflineMeal(candidateMeal)
    );
    const selectedDestinationMealId = bulkMoveTargetByMeal[String(meal.id)]
      || (destinationMealsForSelection[0] ? String(destinationMealsForSelection[0].id) : '');

    if (!isMobileViewport || useDesktopLayoutOnMobile) {
      return (
        <div
          key={meal.id}
          className={`meal-section meal-table-section ${mealIsPendingSync ? 'pending-sync' : ''} ${draggedItem === index ? 'dragging' : ''} ${overIndex === index ? 'over' : ''}`}
          data-meal-dnd-index={index}
          draggable={nativeHtmlDragEnabled}
          onDragOver={(e) => {
            if (draggedFood) {
              if (mealIsPendingSync) return;
              e.preventDefault();
              return;
            }
            if (!mealDragEnabled) return;
            e.preventDefault();
            setOverIndex(index);
          }}
          onDragLeave={() => setOverIndex(null)}
          onDrop={(e) => {
            if (draggedFood) {
              e.preventDefault();
              if (mealIsPendingSync) return;
              handleFoodDrop(e, meal.id, (meal.foods || []).length);
              return;
            }
            if (!mealDragEnabled) return;
            handleDrop(e, index);
          }}
          onDragEnd={(e) => {
            if (!mealDragEnabled) return;
            handleDragEnd(e);
          }}
        >
          <div className="meal-table-toolbar">
            <div className="meal-title-section">
              {!isReadOnly && (
                <span
                  className="meal-title-drag-handle"
                  title="Glisser le repas pour le déplacer"
                  aria-hidden="true"
                  onPointerDown={(event) => startTouchMealDrag(
                    event,
                    index,
                    mealDragEnabled,
                    event.currentTarget.closest('.meal-section')
                  )}
                >
                  <FontAwesomeIcon icon={faGripVertical} />
                </span>
              )}
              <input
                type="text"
                value={mealNameValue}
                className="editable-title meal-table-title-input"
                placeholder="Nom du repas"
                aria-label="Nom du repas"
                readOnly={!isEditingName}
                ref={(el) => {
                  mealNameInputRefs.current[meal.id] = el;
                }}
                onChange={(e) => {
                  if (!isEditingName) return;
                  setMealNameDrafts(prev => ({
                    ...prev,
                    [meal.id]: e.target.value
                  }));
                }}
                onKeyDown={(e) => {
                  if (!isEditingName) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveMealName(meal);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelRenamingMeal(meal.id);
                  }
                }}
              />
              {renderMealTimeControl()}
              {mealIsPendingSync && (
                <span className="meal-pending-badge" title="Ce repas sera synchronisé dès que la connexion revient">
                  Hors ligne · en attente
                </span>
              )}
            </div>
            {!isReadOnly && (
              <div className="meal-table-toolbar-actions">
                <button
                  type="button"
                  draggable={false}
                  title="Copier ce repas"
                  aria-label={`Copier le repas ${meal.name || ''}`.trim()}
                  onClick={() => openCopyCenterForMeal(meal, 'full_day_copy')}
                  className="meal-copy-btn-text"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  Copier
                </button>
                <button
                  type="button"
                  draggable={false}
                  title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
                  aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
                  onClick={() => {
                    if (isEditingName) {
                      saveMealName(meal);
                      return;
                    }
                    startRenamingMeal(meal);
                  }}
                  className="meal-rename-btn-text"
                >
                  <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
                  {isEditingName ? 'Valider' : 'Renommer'}
                </button>
                {isEditingName && (
                  <button
                    type="button"
                    draggable={false}
                    title="Annuler la modification"
                    aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                    onClick={() => cancelRenamingMeal(meal.id)}
                    className="meal-cancel-btn-text"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                    Annuler
                  </button>
                )}
                <button
                  type="button"
                  draggable={false}
                  title="Supprimer ce repas"
                  aria-label={`Supprimer le repas ${meal.name || ''}`.trim()}
                  onClick={() => removeMeal(meal.id, meal.name)}
                  className="meal-delete-btn-text"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Supprimer
                </button>
              </div>
            )}
          </div>
          {!isReadOnly && hasSelectedFoodsInMeal && (
            <div className="meal-table-selection-toolbar" onClick={(event) => event.stopPropagation()}>
              <span className="meal-table-selection-count">
                {selectedFoodsCountForMeal} aliment{selectedFoodsCountForMeal > 1 ? 's' : ''} sélectionné{selectedFoodsCountForMeal > 1 ? 's' : ''}
              </span>
              <div className="meal-table-selection-actions">
                <button
                  type="button"
                  className="meal-table-selection-btn subtle"
                  onClick={() => selectAllFoodsInMeal(meal.id)}
                  disabled={mealIsPendingSync}
                  title="Sélectionner tous les aliments du repas"
                >
                  Tout sélectionner
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'top')}
                  disabled={mealIsPendingSync}
                  title="Placer la sélection en début de repas"
                >
                  Début
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'up')}
                  disabled={mealIsPendingSync}
                  title="Monter la sélection"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                  Monter
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'down')}
                  disabled={mealIsPendingSync}
                  title="Descendre la sélection"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                  Descendre
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn"
                  onClick={() => moveSelectedFoodsInMeal(meal.id, 'bottom')}
                  disabled={mealIsPendingSync}
                  title="Placer la sélection en fin de repas"
                >
                  Fin
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn subtle"
                  onClick={() => clearFoodSelectionForMeal(meal.id)}
                  title="Effacer la sélection"
                >
                  Désélectionner
                </button>
                <button
                  type="button"
                  className="meal-table-selection-btn danger"
                  onClick={() => deleteSelectedFoodsInMeal(meal.id)}
                  disabled={mealIsPendingSync}
                  title="Supprimer la sélection"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Supprimer
                </button>
                <div className="meal-table-selection-move-group">
                  <select
                    className="meal-table-selection-select"
                    value={selectedDestinationMealId}
                    onChange={(event) => setBulkMoveTargetForMeal(meal.id, event.target.value)}
                    disabled={mealIsPendingSync || destinationMealsForSelection.length === 0}
                    aria-label="Choisir le repas destination"
                  >
                    {destinationMealsForSelection.length === 0 ? (
                      <option value="">Aucun repas destination</option>
                    ) : (
                      destinationMealsForSelection.map((candidateMeal) => (
                        <option key={candidateMeal.id} value={candidateMeal.id}>
                          {candidateMeal.name || 'Repas'}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="meal-table-selection-btn"
                    onClick={() => moveSelectedFoodsToMeal(meal.id, selectedDestinationMealId)}
                    disabled={mealIsPendingSync || !selectedDestinationMealId || destinationMealsForSelection.length === 0}
                    title="Déplacer la sélection vers le repas choisi"
                  >
                    Déplacer vers
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="meal-table-wrapper">
            <table className="meal-table">
              <thead>
                <tr>
                  <th className="food-col">Aliment</th>
                  <th>Calories<br /><span>kcal</span></th>
                  <th>Glucides<br /><span>g</span></th>
                  <th>Lipides<br /><span>g</span></th>
                  <th>Protéines<br /><span>g</span></th>
                  <th>Sodium<br /><span>mg</span></th>
                  <th>Sucres<br /><span>g</span></th>
                  {!isReadOnly && (
                    <th className="action-col">
                      Actions
                      <br />
                      <span aria-hidden="true">&nbsp;</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(meal.foods || []).length === 0 ? (
                  <tr
                    className="meal-table-empty"
                    onDragOver={(e) => {
                      if (mealIsPendingSync) return;
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (mealIsPendingSync || !draggedFood) return;
                      handleFoodDrop(e, meal.id, 0);
                    }}
                  >
                    <td colSpan={mealTableColSpan}>
                      <div className="no-food-message meal-table-empty-shared">
                        <p>Aucun aliment</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (meal.foods || []).map((food, fIndex) => {
                    const nutrition = calculateNutrition(food.macronutrients || {}, food.quantity || 100);
                    const foodIngredientsKey = String(food.id || `${meal.id}-${fIndex}`);
                    const foodSelectionKey = getFoodSelectionKey(meal.id, food);
                    const isFoodSelected = selectedFoodKeysForMeal.includes(foodSelectionKey);
                    const foodIngredients = Array.isArray(food.ingredients)
                      ? food.ingredients.filter((ingredient) => String(ingredient || '').trim().length > 0)
                      : [];
                    const detailedNutrition = calculateNutritionForDisplay(food.macronutrients, food.quantity);
                    const detailMacrosForFood = allDetailMacroKeys;
                    const secondaryMacrosForFood = detailMacrosForFood.filter(
                      (macroKey) => !foodPreviewMacros.includes(macroKey)
                    );
                    const hasDetailedMacros = hideUnavailableFoodMacros
                      ? detailMacrosForFood.some((macroKey) => {
                        const value = detailedNutrition[macroKey];
                        const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
                        return Number.isFinite(parsed);
                      })
                      : detailMacrosForFood.length > 0;
                    const hasIngredients = foodIngredients.length > 0;
                    const hasNote = Boolean(food.note && food.note.trim());
                    const hasDetails = hasDetailedMacros || hasIngredients || hasNote || !isPremium;
                    const isDetailsCollapsed = collapsedFoodIngredients[foodIngredientsKey] !== false;
                    const rawOffData = food?.openFoodFactsData
                      || food?.openfoodfactsData
                      || food?.openFoodFacts
                      || food?.offData
                      || food?.sourceData
                      || food?.source_data
                      || null;
                    const offData = parseFoodSourceData(rawOffData);
                    const barcodeKey = getFoodBarcode(food, offData);
                    const foodDisplayName = food.name;
                    const nameKey = foodDisplayName.trim().toLowerCase();
                    const cachedProduct = barcodeKey ? foodImageCache[barcodeKey] : null;
                    const cachedNameProduct = nameKey ? foodImageCache[`name:${nameKey}`] : null;
                    const cachedSourceImage = cachedProduct?.image_front_small_url
                      || cachedProduct?.image_thumb_url
                      || cachedProduct?.image_front_url
                      || cachedProduct?.image_url
                      || cachedNameProduct?.image_front_small_url
                      || cachedNameProduct?.image_thumb_url
                      || cachedNameProduct?.image_front_url
                      || cachedNameProduct?.image_url
                      || '';
                    const rawFoodImageUrl = cachedSourceImage
                      || offData?.image_front_small_url
                      || offData?.image_thumb_url
                      || offData?.image_front_url
                      || offData?.image_url
                      || food?.imageUrl
                      || food?.image_url
                      || food?.image
                      || '';
                    const foodImageUrl = String(rawFoodImageUrl || '').trim();
                    const hasFoodImage = Boolean(foodImageUrl)
                      && foodImageUrl !== 'null'
                      && foodImageUrl !== 'undefined';
                    const fallbackInitial = String(foodDisplayName || '?').trim().charAt(0).toUpperCase();
                    const favoritePayload = buildFavoriteFoodPayload({ food, displayName: foodDisplayName, sourceData: offData });
                    const isFavoriteFood = favoriteFoods.some((favorite) => matchesFavoriteFood(favorite, favoritePayload));

                    return (
                      <React.Fragment key={food.id || `${meal.id}-${fIndex}`}>
                        <tr
                          className={`meal-table-row ${isFoodSelected ? 'selected' : ''} ${draggedFood?.mealId === String(meal.id) && draggedFood?.foodIndex === fIndex ? 'dragging' : ''} ${foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex ? 'over' : ''} ${hasDetails && !isDetailsCollapsed ? 'details-open' : ''}`}
                          data-food-dnd-meal-id={String(meal.id)}
                          data-food-dnd-index={fIndex}
                          draggable={nativeHtmlDragEnabled && !mealIsPendingSync}
                          onDragStart={(e) => {
                            if (!mealDragEnabled) {
                              e.preventDefault();
                              return;
                            }
                            handleFoodDragStart(e, meal.id, fIndex);
                          }}
                          onDragEnd={(e) => {
                            if (!mealDragEnabled) return;
                            handleFoodDragEnd(e);
                          }}
                          onDragOver={(e) => {
                            if (!mealDragEnabled) return;
                            e.preventDefault();
                            e.stopPropagation();
                            setFoodOverTarget({ mealId: String(meal.id), foodIndex: fIndex });
                          }}
                          onDragLeave={(e) => {
                            e.stopPropagation();
                            if (foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex) {
                              setFoodOverTarget(null);
                            }
                          }}
                          onDrop={(e) => {
                            if (!mealDragEnabled) return;
                            handleFoodDrop(e, meal.id, fIndex);
                          }}
                          aria-expanded={hasDetails ? !isDetailsCollapsed : undefined}
                        >
                          <td className="food-name-cell">
                            <div className="food-name-with-image">
                              {!isReadOnly && (
                                <label
                                  className="meal-table-select-toggle"
                                  onClick={(event) => event.stopPropagation()}
                                  title={isFoodSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isFoodSelected}
                                    onChange={() => toggleFoodSelection(meal.id, foodSelectionKey)}
                                    aria-label={`${isFoodSelected ? 'Retirer' : 'Ajouter'} ${foodDisplayName || 'cet aliment'} de la sélection`}
                                    disabled={mealIsPendingSync}
                                  />
                                </label>
                              )}
                              {!isReadOnly && (
                                <span
                                  className="meal-table-drag-handle"
                                  title="Glisser pour déplacer"
                                  aria-hidden="true"
                                  onPointerDown={(event) => startTouchFoodDrag(
                                    event,
                                    meal.id,
                                    fIndex,
                                    mealDragEnabled,
                                    event.currentTarget.closest('.meal-table-row')
                                  )}
                                >
                                  <FontAwesomeIcon icon={faGripVertical} />
                                </span>
                              )}
                              {hasFoodImage ? (
                                <button
                                  type="button"
                                  className="meal-table-image-btn"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openFoodImagePreview({
                                      food,
                                      sourceData: offData,
                                      cachedProduct,
                                      cachedNameProduct,
                                      imageUrl: foodImageUrl,
                                      displayName: foodDisplayName,
                                      barcode: barcodeKey,
                                    });
                                  }}
                                  aria-label={`Agrandir l'image de ${foodDisplayName || 'cet aliment'}`}
                                >
                                  <img
                                    src={foodImageUrl}
                                    alt={foodDisplayName || 'Aliment'}
                                    className="meal-table-food-image"
                                    loading="lazy"
                                    onError={(event) => {
                                      event.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </button>
                              ) : (
                                <div className="meal-table-food-image placeholder">{fallbackInitial}</div>
                              )}
                              <div className="food-name-content">
                                <div className="food-name-main">{foodDisplayName || 'Aliment'}</div>
                                <div className="food-name-sub">
                                  {Math.round(Number(food.quantity) || 100)} g
                                </div>
                                {hasDetails && (
                                  <button
                                    type="button"
                                    className={`food-row-hint ${isDetailsCollapsed ? '' : 'open'}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleFoodIngredients(foodIngredientsKey);
                                    }}
                                    aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                                  >
                                    <FontAwesomeIcon icon={faChevronDown} />
                                    {isDetailsCollapsed ? 'Détails' : 'Masquer'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{Math.round(Number(nutrition.energy || nutrition['energy-kcal'] || 0))}</td>
                          <td>{formatGramCell(nutrition.carbohydrates)}</td>
                          <td>{formatGramCell(nutrition.fat)}</td>
                          <td>{formatGramCell(nutrition.proteins)}</td>
                          <td>{formatSodiumMgCell(nutrition.sodium)}</td>
                          <td>{formatGramCell(nutrition.sugars)}</td>
                          {!isReadOnly && (
                            <td className="meal-table-actions-cell">
                              {!isReadOnly && (
                                <div className="meal-table-actions" onClick={(event) => event.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn note"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (mealIsPendingSync) return;
                                      editFoodNote(food.id, food.note, food.name);
                                    }}
                                    disabled={mealIsPendingSync}
                                    title="Ajouter/modifier une note"
                                    aria-label={`Modifier la note de ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faStickyNote} />
                                    Note
                                  </button>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn edit"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (mealIsPendingSync) return;
                                      addFoodToMeal(meal.id, index, food.id);
                                    }}
                                    disabled={mealIsPendingSync}
                                    title="Modifier cet aliment"
                                    aria-label={`Modifier ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                    Éditer
                                  </button>
                                  <button
                                    type="button"
                                    className={`food-mobile-action-btn favorite ${isFavoriteFood ? 'active' : ''}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (mealIsPendingSync) return;
                                      addFoodToFavorites(food, foodDisplayName, offData);
                                    }}
                                    disabled={mealIsPendingSync}
                                    title={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                    aria-label={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                  >
                                    <FontAwesomeIcon icon={faStar} />
                                    {isFavoriteFood ? 'Retirer' : 'Ajouter'}
                                  </button>
                                  <button
                                    type="button"
                                    className="food-mobile-action-btn delete"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (mealIsPendingSync) return;
                                      removeFood(food.id, food.name);
                                    }}
                                    disabled={mealIsPendingSync}
                                    title="Supprimer cet aliment"
                                    aria-label={`Supprimer ${foodDisplayName || 'cet aliment'}`}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Supprimer
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        {hasDetails && !isDetailsCollapsed && (
                          <tr className="meal-table-details-row">
                            <td colSpan={mealTableColSpan}>
                              <div className="meal-table-details-content">
                                {hasNote && (
                                  <div className="meal-table-note">
                                    <div className="meal-table-note-label">
                                      <FontAwesomeIcon icon={faStickyNote} />
                                      Note
                                    </div>
                                    <p>{food.note}</p>
                                  </div>
                                )}

                                {hasDetailedMacros && (
                                  <div className="meal-table-details-macros">
                                    <FoodMacros
                                      macronutrients={detailedNutrition}
                                      mainMacros={foodPreviewMacros}
                                      displayedMacros={[]}
                                      showToggle={false}
                                      hideUnavailableMacros={hideUnavailableFoodMacros}
                                    />
                                    {secondaryMacrosForFood.length > 0 && (
                                      <FoodMacros
                                        macronutrients={detailedNutrition}
                                        mainMacros={[]}
                                        displayedMacros={secondaryMacrosForFood}
                                        renderMainMacros={false}
                                        expanded
                                        showToggle={false}
                                        hideUnavailableMacros={hideUnavailableFoodMacros}
                                        showSectionTitle
                                      />
                                    )}
                                  </div>
                                )}

                                {hasIngredients ? (
                                  <div className="food-ingredients-block">
                                    <div className="food-ingredients-title">Ingrédients</div>
                                    <div className="food-ingredients">
                                      <div className="food-ingredients-list">
                                        {foodIngredients.map((ingredient, ingredientIndex) => (
                                          <span
                                            key={`${foodIngredientsKey}-ingredient-${ingredientIndex}`}
                                            className="food-ingredient-chip"
                                          >
                                            {ingredient}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }))}
              </tbody>
              <tfoot>
                <tr className="meal-table-total-row">
                  <td className="meal-table-total-title-cell">
                    <span className="meal-table-total-title">Total du repas</span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-calories" title="Calories totales du repas">
                      <span className="meal-total-value">{Math.round(totals.energy)}</span>
                      <span className="meal-total-unit">kcal</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-carbs" title="Glucides totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.carbohydrates)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-fat" title="Lipides totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.fat)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-proteins" title="Protéines totales du repas">
                      <span className="meal-total-value">{formatGramCell(totals.proteins)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-sodium" title="Sodium total du repas">
                      <span className="meal-total-value">{formatSodiumMgCell(totals.sodium)}</span>
                      <span className="meal-total-unit">mg</span>
                    </span>
                  </td>
                  <td>
                    <span className="meal-total-pill meal-total-pill-sugars" title="Sucres totaux du repas">
                      <span className="meal-total-value">{formatGramCell(totals.sugars)}</span>
                      <span className="meal-total-unit">g</span>
                    </span>
                  </td>
                  {!isReadOnly && <td></td>}
                </tr>
              </tfoot>
            </table>
            <div className="meal-table-footer-actions">
              <div className="meal-table-link-group">
                {!isReadOnly && (
                  <>
                    <button
                      type="button"
                      className="meal-table-link meal-table-link-pill"
                      onClick={() => {
                        setLastActiveMealIndex(index);
                        addFoodToMeal(meal.id, index);
                      }}
                      disabled={mealIsPendingSync}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Ajouter un aliment
                    </button>
                    <button
                      type="button"
                      className="meal-table-link meal-table-link-pill search"
                      onClick={() => {
                        setLastActiveMealIndex(index);
                        searchFoodInDB(index);
                      }}
                      disabled={mealIsPendingSync}
                    >
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                      Rechercher un aliment
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={meal.id}
        className={`meal-section mobile-diary-meal ${mealIsPendingSync ? 'pending-sync' : ''} ${draggedItem === index ? 'dragging' : ''} ${overIndex === index ? 'over' : ''}`}
        data-meal-dnd-index={index}
        draggable={nativeHtmlDragEnabled}
        onDragStart={(e) => {
          if (!mealDragEnabled) {
            e.preventDefault();
            return;
          }
          handleDragStart(e, index);
        }}
        onDragOver={(e) => {
          if (!mealDragEnabled) {
            return;
          }
          e.preventDefault();
          setOverIndex(index);
        }}
        onDragLeave={() => setOverIndex(null)}
        onDrop={(e) => {
          if (!mealDragEnabled) return;
          handleDrop(e, index);
        }}
        onDragEnd={(e) => {
          if (!mealDragEnabled) return;
          handleDragEnd(e);
        }}
      >
        <div
          className="meal-header"
          onClick={() => {
            if (isMobileViewport) {
              setLastActiveMealIndex(index);
            }
          }}
        >
          <div className="meal-title-section">
            <input
              type="text"
              value={mealNameValue}
              className="editable-title"
              placeholder="Nom du repas"
              aria-label="Nom du repas"
              readOnly={!isEditingName}
              ref={(el) => {
                mealNameInputRefs.current[meal.id] = el;
              }}
              onChange={(e) => {
                if (!isEditingName) return;
                setMealNameDrafts(prev => ({
                  ...prev,
                  [meal.id]: e.target.value
                }));
              }}
              onKeyDown={(e) => {
                if (!isEditingName) return;
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveMealName(meal);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelRenamingMeal(meal.id);
                }
              }}
            />
            {renderMealTimeControl()}
            {mealIsPendingSync && (
              <span className="meal-pending-badge" title="Ce repas sera synchronisé dès que la connexion revient">
                Hors ligne · en attente
              </span>
            )}
          </div>
          {isMobileViewport ? (
            <div className="meal-mobile-header-tools">
              <div className="meal-mobile-sort-controls" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="meal-mobile-sort-btn"
                  onClick={() => moveMealByOffset(index, -1)}
                  disabled={mealIsPendingSync || index === 0}
                  aria-label={`Monter le repas ${meal.name || ''}`.trim()}
                  title="Monter ce repas"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                  type="button"
                  className="meal-mobile-sort-btn"
                  onClick={() => moveMealByOffset(index, 1)}
                  disabled={mealIsPendingSync || index === filteredMeals.length - 1}
                  aria-label={`Descendre le repas ${meal.name || ''}`.trim()}
                  title="Descendre ce repas"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
              </div>
              {!isReadOnly && (
                <button
                  type="button"
                  className={`meal-actions-toggle ${isMealActionsOpen ? 'active' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenMealActionsId((prev) => (prev === meal.id ? null : meal.id));
                  }}
                  aria-label={`Actions pour le repas ${meal.name || ''}`.trim()}
                >
                  Actions
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                    <path
                      d="M6.5 9.5 12 15l5.5-5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div className="meal-actions">
              <button
                type="button"
                draggable={false}
                title="Copier ce repas"
                aria-label={`Copier le repas ${meal.name || ''}`.trim()}
                onClick={() => openCopyCenterForMeal(meal, 'full_day_copy')}
                className="meal-copy-btn-text"
              >
                <FontAwesomeIcon icon={faCopy} />
                Copier
              </button>
              <button
                type="button"
                draggable={false}
                title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
                aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
                onClick={() => {
                  if (isEditingName) {
                    saveMealName(meal);
                    return;
                  }
                  startRenamingMeal(meal);
                }}
                className="meal-rename-btn-text"
              >
                <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
                {isEditingName ? 'Valider' : 'Renommer'}
              </button>
              {isEditingName && (
                <button
                  type="button"
                  draggable={false}
                  title="Annuler la modification"
                  aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                  onClick={() => cancelRenamingMeal(meal.id)}
                  className="meal-cancel-btn-text"
                >
                  <FontAwesomeIcon icon={faXmark} />
                  Annuler
                </button>
              )}
              <button
                type="button"
                draggable={false}
                title="Supprimer ce repas"
                aria-label={`Supprimer le repas ${meal.name || ""}`.trim()}
                onClick={() => removeMeal(meal.id, meal.name)}
                className="meal-delete-btn-text"
              >
                <FontAwesomeIcon icon={faTrash} />
                Supprimer
              </button>
            </div>
          )}
        </div>
        {isMobileViewport && (
          <div className={`meal-actions-panel ${isMealActionsOpen ? 'open' : ''}`}>
            <button
              type="button"
              draggable={false}
              title="Copier ce repas"
              aria-label={`Copier le repas ${meal.name || ''}`.trim()}
              onClick={(event) => {
                event.stopPropagation();
                openCopyCenterForMeal(meal, 'full_day_copy');
                setOpenMealActionsId(null);
              }}
              className="meal-copy-btn-text"
            >
              <FontAwesomeIcon icon={faCopy} />
              Copier
            </button>
            <button
              type="button"
              draggable={false}
              title={isEditingName ? 'Valider le nouveau nom' : 'Renommer ce repas'}
              aria-label={isEditingName ? `Valider le nom du repas ${meal.name || ''}`.trim() : `Renommer le repas ${meal.name || ''}`.trim()}
              onClick={(event) => {
                event.stopPropagation();
                if (isEditingName) {
                  saveMealName(meal);
                  return;
                }
                startRenamingMeal(meal);
                setOpenMealActionsId(meal.id);
              }}
              className="meal-rename-btn-text"
            >
              <FontAwesomeIcon icon={isEditingName ? faCheck : faPen} />
              {isEditingName ? 'Valider' : 'Renommer'}
            </button>
            {isEditingName && (
              <button
                type="button"
                draggable={false}
                title="Annuler la modification"
                aria-label={`Annuler le renommage du repas ${meal.name || ''}`.trim()}
                onClick={(event) => {
                  event.stopPropagation();
                  cancelRenamingMeal(meal.id);
                }}
                className="meal-cancel-btn-text"
              >
                <FontAwesomeIcon icon={faXmark} />
                Annuler
              </button>
            )}
            <button
              type="button"
              draggable={false}
              title="Supprimer ce repas"
              aria-label={`Supprimer le repas ${meal.name || ""}`.trim()}
              onClick={(event) => {
                event.stopPropagation();
                removeMeal(meal.id, meal.name);
                setOpenMealActionsId(null);
              }}
              className="meal-delete-btn-text"
            >
              <FontAwesomeIcon icon={faTrash} />
              Supprimer
            </button>
          </div>
        )}

        <div className="meal-nutrition-summary">
          <div className="meal-summary-header">
            <div className="meal-summary-icon">
              <span className="summary-title">Total</span>
            </div>
            <div className="total-calories">
              <span className="calories-number">{mealCalories}</span>
              <span className="calories-unit">kcal</span>
            </div>
          </div>
          <div className="meal-total-macros-grid" aria-label="Macros totales du repas">
            <div className="meal-total-macro-chip proteins">
              <span className="meal-total-macro-label">Protéines</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.proteins)} g</span>
            </div>
            <div className="meal-total-macro-chip carbs">
              <span className="meal-total-macro-label">Glucides</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.carbohydrates)} g</span>
            </div>
            <div className="meal-total-macro-chip fat">
              <span className="meal-total-macro-label">Lipides</span>
              <span className="meal-total-macro-value">{formatGramCell(totals.fat)} g</span>
            </div>
          </div>
        </div>

        <div className="foods-list">
          {meal.foods.length === 0 ? (
            <div className="no-food-message">
              <p>Aucun aliment</p>
            </div>
          ) : (
            meal.foods.map((food, fIndex) => (
              <div
                className={`food-entry mobile-diary-food-entry ${draggedFood?.mealId === String(meal.id) && draggedFood?.foodIndex === fIndex ? 'dragging' : ''} ${foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex ? 'over' : ''}`}
                key={food.id || fIndex}
                data-food-dnd-meal-id={String(meal.id)}
                data-food-dnd-index={fIndex}
                draggable={false}
                onDragStart={(e) => {
                  if (!mealDragEnabled) {
                    e.preventDefault();
                    return;
                  }
                  handleFoodDragStart(e, meal.id, fIndex);
                }}
                onDragEnd={(e) => {
                  if (!mealDragEnabled) return;
                  handleFoodDragEnd(e);
                }}
                onDragOver={(e) => {
                  if (!mealDragEnabled) return;
                  e.preventDefault();
                  setFoodOverTarget({ mealId: String(meal.id), foodIndex: fIndex });
                }}
                onDragLeave={() => {
                  if (foodOverTarget?.mealId === String(meal.id) && foodOverTarget?.foodIndex === fIndex) {
                    setFoodOverTarget(null);
                  }
                }}
                onDrop={(e) => {
                  if (!mealDragEnabled) return;
                  handleFoodDrop(e, meal.id, fIndex);
                }}
              >
                {(() => {
                  const foodIngredientsKey = String(food.id || `${meal.id}-${fIndex}`);
                  const foodIngredients = Array.isArray(food.ingredients)
                    ? food.ingredients.filter((ingredient) => String(ingredient || '').trim().length > 0)
                    : [];
                  const detailedNutrition = calculateNutritionForDisplay(food.macronutrients, food.quantity);
                  const hasIngredients = foodIngredients.length > 0;
                  const isDetailsCollapsed = collapsedFoodIngredients[foodIngredientsKey] !== false;
                  const isMobileCompact = isMobileViewport;
                  const detailMacrosForFood = allDetailMacroKeys;
                  const secondaryMacrosForFood = detailMacrosForFood.filter(
                    (macroKey) => !foodPreviewMacros.includes(macroKey)
                  );
                  const hasDetailedMacros = hideUnavailableFoodMacros
                    ? detailMacrosForFood.some((macroKey) => {
                      const value = detailedNutrition[macroKey];
                      const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
                      return Number.isFinite(parsed);
                    })
                    : detailMacrosForFood.length > 0;
                  const hasDetails = hasDetailedMacros || hasIngredients || !isPremium;
                  const foodCalories = Math.round((calculateNutrition(food.macronutrients || {}, food.quantity || 100).energy) || 0);
                  const rawOffData = food?.openFoodFactsData
                    || food?.openfoodfactsData
                    || food?.openFoodFacts
                    || food?.offData
                    || food?.sourceData
                    || food?.source_data
                    || null;
                  const offData = parseFoodSourceData(rawOffData);
                  const foodDisplayName = food.name;
                  const nameKey = foodDisplayName.trim().toLowerCase();
                  const barcodeKey = getFoodBarcode(food, offData);
                  const cachedProduct = barcodeKey ? foodImageCache[barcodeKey] : null;
                  const cachedNameProduct = nameKey ? foodImageCache[`name:${nameKey}`] : null;
                  const rawFoodImageUrl = cachedProduct?.image_front_small_url
                    || cachedProduct?.image_thumb_url
                    || cachedProduct?.image_front_url
                    || cachedProduct?.image_url
                    || cachedNameProduct?.image_front_small_url
                    || cachedNameProduct?.image_thumb_url
                    || cachedNameProduct?.image_front_url
                    || cachedNameProduct?.image_url
                    || offData?.image_front_small_url
                    || offData?.image_thumb_url
                    || offData?.image_front_url
                    || offData?.image_url
                    || food?.imageUrl
                    || food?.image_url
                    || food?.image
                    || '';
                  const foodImageUrl = String(rawFoodImageUrl || '').trim();
                  const hasFoodImage = Boolean(foodImageUrl)
                    && foodImageUrl !== 'null'
                    && foodImageUrl !== 'undefined';
                  const fallbackInitial = String(foodDisplayName || '?').trim().charAt(0).toUpperCase();
                  const showMobileDetails = isMobileCompact && !isDetailsCollapsed;
                  const hasPreviousMeal = index > 0 && !isPendingOfflineMeal(filteredMeals[index - 1]);
                  const hasNextMeal = index < filteredMeals.length - 1 && !isPendingOfflineMeal(filteredMeals[index + 1]);
                  const canMoveFoodUp = !mealIsPendingSync && (fIndex > 0 || hasPreviousMeal);
                  const canMoveFoodDown = !mealIsPendingSync && (fIndex < (meal.foods.length - 1) || hasNextMeal);
                  const favoritePayload = buildFavoriteFoodPayload({ food, displayName: foodDisplayName, sourceData: offData });
                  const isFavoriteFood = favoriteFoods.some((favorite) => matchesFavoriteFood(favorite, favoritePayload));

                  return (
                    <>
                      {isMobileViewport ? (
                        <div className="food-entry-header mobile-compact">
                          <div className="food-mobile-row">
                            <div className="food-mobile-main">
                              {hasFoodImage ? (
                                <button
                                  type="button"
                                  className="food-mobile-image-button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    openFoodImagePreview({
                                      food,
                                      sourceData: offData,
                                      cachedProduct,
                                      cachedNameProduct,
                                      imageUrl: foodImageUrl,
                                      displayName: foodDisplayName,
                                      barcode: barcodeKey,
                                    });
                                  }}
                                  aria-label={`Agrandir l'image de ${foodDisplayName || 'cet aliment'}`}
                                >
                                  <img
                                    src={foodImageUrl}
                                    alt={foodDisplayName || 'Aliment'}
                                    className="food-mobile-image"
                                    loading="lazy"
                                    onError={(event) => {
                                      event.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </button>
                              ) : (
                                <div className="food-mobile-image placeholder">
                                  {fallbackInitial}
                                </div>
                              )}
                              <div className="food-mobile-name-wrap">
                                <div className="food-mobile-name" title={foodDisplayName || ''}>
                                  {foodDisplayName}
                                </div>
                                <div className="food-mobile-quantity-inline" title="Quantité">
                                  {(food.quantity || 100)}g
                                </div>
                              </div>
                            </div>
                            <div className="food-mobile-meta">
                              <div className="food-mobile-calories">
                                <span className="calories-amount">{foodCalories}</span>
                                <span className="calories-text">kcal</span>
                              </div>
                              {hasDetails && (
                                <button
                                  type="button"
                                  className={`food-ingredients-toggle compact ${isDetailsCollapsed ? '' : 'active'}`}
                                  onClick={() => toggleFoodIngredients(foodIngredientsKey)}
                                  aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                                >
                                  Détails
                                  <svg viewBox="0 0 24 24" width="14" height="14">
                                    <path
                                      d="M6.5 9.5 12 15l5.5-5.5"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="food-mobile-actions">
                            <div className="food-mobile-sort-controls">
                              <button
                                type="button"
                                className="food-mobile-sort-btn"
                                onClick={() => moveFoodByOffset(meal.id, fIndex, -1)}
                                disabled={!canMoveFoodUp}
                                aria-label={`Monter l'aliment ${foodDisplayName || ''}`.trim()}
                                title="Monter cet aliment"
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </button>
                              <button
                                type="button"
                                className="food-mobile-sort-btn"
                                onClick={() => moveFoodByOffset(meal.id, fIndex, 1)}
                                disabled={!canMoveFoodDown}
                                aria-label={`Descendre l'aliment ${foodDisplayName || ''}`.trim()}
                                title="Descendre cet aliment"
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </button>
                            </div>
                            {!isReadOnly && (
                              <div className="food-mobile-main-actions">
                                <button
                                  type="button"
                                  className={`food-mobile-action-btn favorite ${isFavoriteFood ? 'active' : ''}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (mealIsPendingSync) return;
                                    addFoodToFavorites(food, foodDisplayName, offData);
                                  }}
                                  disabled={mealIsPendingSync}
                                  title={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                  aria-label={isFavoriteFood ? `Retirer ${foodDisplayName || 'cet aliment'} des favoris` : `Ajouter ${foodDisplayName || 'cet aliment'} aux favoris`}
                                >
                                  <FontAwesomeIcon icon={faStar} />
                                  {isFavoriteFood ? 'Retirer' : 'Ajouter'}
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn note"
                                  onClick={() => {
                                    if (mealIsPendingSync) return;
                                    editFoodNote(food.id, food.note, food.name);
                                  }}
                                  disabled={mealIsPendingSync}
                                  aria-label={`Modifier la note de ${foodDisplayName || 'cet aliment'}`}
                                  title="Ajouter ou modifier une note"
                                >
                                  <FontAwesomeIcon icon={faStickyNote} />
                                  Note
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn edit"
                                  onClick={() => {
                                    if (mealIsPendingSync) return;
                                    addFoodToMeal(meal.id, index, food.id);
                                  }}
                                  disabled={mealIsPendingSync}
                                  aria-label={`Modifier ${foodDisplayName || 'cet aliment'}`}
                                  title="Modifier l'aliment"
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                  Éditer
                                </button>
                                <button
                                  type="button"
                                  className="food-mobile-action-btn delete"
                                  onClick={() => {
                                    if (mealIsPendingSync) return;
                                    removeFood(food.id, food.name);
                                  }}
                                  disabled={mealIsPendingSync}
                                  aria-label={`Supprimer ${foodDisplayName || 'cet aliment'}`}
                                  title="Supprimer l'aliment"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="food-entry-header">
                          <div className="food-info">
                            <h4 className="food-name">{foodDisplayName}</h4>
                            <div className="food-meta-row">
                              <div className="food-quantity" title="Quantité">
                                {food.quantity || 100}g
                              </div>
                            </div>
                            <div className="food-calories">
                              <span className="calories-amount">{foodCalories}</span>
                              <span className="calories-text">kcal</span>
                            </div>
                          </div>
                          {!isReadOnly && (
                            <div className="food-actions">
                              <button
                                type="button"
                                className="food-mobile-action-btn note"
                                onClick={() => editFoodNote(food.id, food.note, food.name)}
                                title="Ajouter/modifier une note"
                              >
                                <FontAwesomeIcon icon={faStickyNote} />
                                Note
                              </button>
                              <button
                                type="button"
                                className="food-mobile-action-btn edit"
                                onClick={() => addFoodToMeal(meal.id, index, food.id)}
                                title="Modifier cet aliment"
                              >
                                <FontAwesomeIcon icon={faPen} />
                                Éditer
                              </button>
                              <button
                                type="button"
                                className="food-mobile-action-btn delete"
                                onClick={() => removeFood(food.id, food.name)}
                                title="Supprimer cet aliment"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {(!isMobileCompact || showMobileDetails) && food.note && food.note.trim() && (
                        <FoodNote>
                          <NoteIcon>
                            <FontAwesomeIcon icon={faStickyNote} />
                            Note
                          </NoteIcon>
                          <NoteText>{food.note}</NoteText>
                        </FoodNote>
                      )}

                      {!isMobileCompact && (
                        <FoodMacros
                          macronutrients={detailedNutrition}
                          mainMacros={foodPreviewMacros}
                          displayedMacros={[]}
                          showToggle={false}
                          hideUnavailableMacros={hideUnavailableFoodMacros}
                        />
                      )}

                      {hasDetails && !isMobileViewport && (
                        <button
                          type="button"
                          className={`food-ingredients-toggle ${isDetailsCollapsed ? '' : 'active'}`}
                          onClick={() => toggleFoodIngredients(foodIngredientsKey)}
                          aria-label={isDetailsCollapsed ? 'Afficher les détails de l’aliment' : 'Masquer les détails de l’aliment'}
                        >
                          Détails
                          <svg viewBox="0 0 24 24" width="24" height="24">
                            <path
                              d="M6.5 9.5 12 15l5.5-5.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                      {!isDetailsCollapsed && (
                        <>
                          {hasDetailedMacros && (
                            <>
                              {isMobileCompact && (
                                <FoodMacros
                                  macronutrients={detailedNutrition}
                                  mainMacros={foodPreviewMacros}
                                  displayedMacros={[]}
                                  showToggle={false}
                                  hideUnavailableMacros={hideUnavailableFoodMacros}
                                />
                              )}
                              {secondaryMacrosForFood.length > 0 && (
                                <>
                                  {isMobileCompact && (
                                    <div className="food-macros-section-title">Macros secondaires</div>
                                  )}
                                  <FoodMacros
                                    macronutrients={detailedNutrition}
                                    mainMacros={[]}
                                    displayedMacros={secondaryMacrosForFood}
                                    renderMainMacros={false}
                                    expanded
                                    showToggle={false}
                                    hideUnavailableMacros={hideUnavailableFoodMacros}
                                    showSectionTitle={!isMobileCompact}
                                  />
                                </>
                              )}
                            </>
                          )}
                          {hasIngredients ? (
                            <div className="food-ingredients-block">
                              <div className="food-ingredients-title">Ingrédients</div>
                              <div className="food-ingredients">
                                <div className="food-ingredients-list">
                                  {foodIngredients.map((ingredient, ingredientIndex) => (
                                    <span
                                      key={`${foodIngredientsKey}-ingredient-${ingredientIndex}`}
                                      className="food-ingredient-chip"
                                    >
                                      {ingredient}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            ))
          )}
        </div>

        {!isReadOnly && (
          <div className="add-food-options mobile-diary-add-food-options">
            <button
              onClick={() => {
                setLastActiveMealIndex(index);
                addFoodToMeal(meal.id, index);
              }}
              className="add-food-btn manual-btn"
              disabled={mealIsPendingSync}
              title={mealIsPendingSync ? 'Disponible après synchronisation du repas' : 'Ajouter un aliment'}
            >
              Ajouter
            </button>
            <button
              onClick={() => {
                setLastActiveMealIndex(index);
                searchFoodInDB(index);
              }}
              className="add-food-btn search-btn"
              disabled={mealIsPendingSync}
              title={mealIsPendingSync ? 'Disponible après synchronisation du repas' : 'Rechercher un aliment'}
            >
              Rechercher
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatMobileCalories = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0';
    }
    return new Intl.NumberFormat('fr-FR').format(Math.round(numeric));
  };

  const renderMobileCalorieOverview = () => {
    const {
      hasConfiguredGoal,
      goalCalories,
      foodCalories,
      remainingCalories,
    } = mobileCalorieOverview;

    return (
      <section className="mobile-calorie-overview-card" aria-label="Calories restantes">
        <div className="mobile-calorie-overview-head">
          <h3>Calories restantes</h3>
          {!hasConfiguredGoal && (
            <span className="mobile-calorie-goal-hint">Objectif auto</span>
          )}
        </div>
        <div className="mobile-calorie-overview-math">
          <div className="mobile-calorie-metric">
            <span className="metric-value">{formatMobileCalories(goalCalories)}</span>
            <span className="metric-label">Objectif</span>
          </div>
          <span className="metric-operator" aria-hidden="true">-</span>
          <div className="mobile-calorie-metric">
            <span className="metric-value">{formatMobileCalories(foodCalories)}</span>
            <span className="metric-label">Consommé</span>
          </div>
          <span className="metric-operator" aria-hidden="true">=</span>
          <div className={`mobile-calorie-metric metric-result ${remainingCalories >= 0 ? 'positive' : 'negative'}`}>
            <span className="metric-value">{formatMobileCalories(remainingCalories)}</span>
            <span className="metric-label">Restant</span>
          </div>
        </div>
      </section>
    );
  };

  const renderSummaryElement = () => {
    const advancedGoalsEnabled =
      isPremium
      && (
        nutritionalGoals.advancedMacroGoalsEnabled === true
        || nutritionalGoals.advancedMacroGoalsEnabled === 'true'
      );
    const selectedFocusedSummaryMacros = (Array.isArray(summaryMacros) ? summaryMacros : [])
      .filter((macroKey) => SUMMARY_FOCUSED_MACROS.has(macroKey))
      .filter((macroKey) => advancedGoalsEnabled || !ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey));
    const effectiveSummaryMacros = selectedFocusedSummaryMacros;

    if (!effectiveSummaryMacros || effectiveSummaryMacros.length === 0) {
      return (
        <SummaryWrapper>
          <SummaryTitle>Résumé de la journée</SummaryTitle>
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
            Aucune macro sélectionnée pour le résumé. Configurez vos macros dans les paramètres.
          </div>
        </SummaryWrapper>
      );
    }

    const totals = {};

    Object.values(SUMMARY_MACRO_MAPPING).forEach(config => {
      totals[config.dataKey] = 0;
    });

    filteredMeals.forEach(meal => {
      meal.foods.forEach(food => {
        const actual = calculateNutrition(food.macronutrients || {}, food.quantity || 100);

        Object.keys(actual).forEach(key => {
          if (!key.endsWith('_100g') && !key.endsWith('_serving') && !key.endsWith('_unit') && !key.endsWith('_value')) {
            totals[key] = (totals[key] || 0) + (actual[key] || 0);
          }
        });
      });
    });

    // Le sodium est stocké en grammes côté aliment (comme le sait déjà
    // formatSodiumMgCell pour le tableau de repas), mais l'objectif
    // targetSodium est saisi en mg dans les Paramètres. Sans conversion,
    // le résumé comparait des grammes à des milligrammes.
    if (totals.sodium !== undefined) {
      totals.sodium = totals.sodium * 1000;
    }

    const directEnergyKcal = Number(totals['energy-kcal'] || totals.energy || totals.calories || 0);
    const chartData = effectiveSummaryMacros
      .map((macroKey) => {
        const macroConfig = SUMMARY_MACRO_MAPPING[macroKey];
        if (!macroConfig) return null;

        const current = Math.max(0, Number(totals[macroConfig.dataKey] || 0));
        const goalRaw = macroConfig.goalKey ? Number(nutritionalGoals?.[macroConfig.goalKey]) : NaN;
        const hasGoal = Number.isFinite(goalRaw) && goalRaw > 0;
        const normalizedValue = hasGoal ? (current / goalRaw) : current;
        const chartValue = Math.max(normalizedValue, 0.08); // conserve toutes les macros visibles

        return {
          key: macroKey,
          label: String(macroConfig.label || macroKey).replace(/^\w/u, (ch) => ch.toUpperCase()),
          color: macroConfig.color || '#64748b',
          unit: macroConfig.unit || '',
          current,
          goal: hasGoal ? goalRaw : null,
          value: Number.isFinite(chartValue) ? chartValue : 0.08,
        };
      })
      .filter(Boolean);

    const chartTotal = chartData.reduce((sum, macro) => sum + (macro.value || 0), 0);
    const chartDataByKey = new Map(chartData.map((macro) => [macro.key, macro]));

    const formatSummaryLegendValue = (value, unit) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return '0';
      }
      if (unit === 'kcal' || unit === 'kJ' || unit === 'mg') {
        return `${Math.round(numericValue)}`;
      }
      return numericValue.toFixed(1);
    };

    const summaryLegendData = effectiveSummaryMacros
      .map((macroKey) => {
        const macroConfig = SUMMARY_MACRO_MAPPING[macroKey];
        if (!macroConfig) return null;

        const chartMacro = chartDataByKey.get(macroKey) || null;
        const current = totals[macroConfig.dataKey] || 0;
        const percentage = chartMacro && chartTotal > 0
          ? (chartMacro.value / chartTotal) * 100
          : null;

        return {
          key: macroKey,
          label: String(macroConfig.label || macroKey).replace(/^\w/u, (ch) => ch.toUpperCase()),
          unit: macroConfig.unit || '',
          current,
          percentage,
          color: chartMacro?.color || macroConfig.color || '#64748b',
          goal: chartMacro?.goal || null,
        };
      })
      .filter(Boolean);

    const tooltipMacroKey = summaryChartTooltip?.key || summaryChartHoverKey || null;
    const activeTooltipMacro = tooltipMacroKey ? chartDataByKey.get(tooltipMacroKey) : null;
    const activeTooltipPercentage =
      activeTooltipMacro && chartTotal > 0
        ? (activeTooltipMacro.value / chartTotal) * 100
        : 0;

    const renderPieChart = () => {
      if (chartTotal === 0) {
        return (
          <div className="empty-chart">
            <svg viewBox="0 0 24 24" width="48" height="48" style={{ fill: '#94a3b8', opacity: 0.4 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '0.5rem' }}>Aucune donnée</p>
          </div>
        );
      }

      const size = 280;
      const center = size / 2;
      const radius = size / 2 - 20;
      const innerRadius = radius * 0.56;

      let currentAngle = -90;

      const slices = chartData.map((macro) => {
        const value = macro.value || 0;
        const percentage = (value / chartTotal) * 100;
        const angle = (percentage / 100) * 360;

        if (value === 0) return null;

        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        currentAngle = endAngle;

        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        const x1 = center + radius * Math.cos(startRad);
        const y1 = center + radius * Math.sin(startRad);
        const x2 = center + radius * Math.cos(endRad);
        const y2 = center + radius * Math.sin(endRad);

        const largeArc = angle > 180 ? 1 : 0;

        const path = [
          `M ${center} ${center}`,
          `L ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
          'Z'
        ].join(' ');

        return (
          <g key={macro.key}>
            <path
              className="summary-chart-slice"
              d={path}
              fill={macro.color}
              stroke="white"
              strokeWidth="4"
              onMouseEnter={(event) => {
                setSummaryChartHoverKey(macro.key);
                const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                if (svgRect) {
                  setSummaryChartTooltip({
                    key: macro.key,
                    x: Math.max(18, Math.min(svgRect.width - 18, event.clientX - svgRect.left)),
                    y: Math.max(18, Math.min(svgRect.height - 18, event.clientY - svgRect.top)),
                  });
                }
              }}
              onMouseMove={(event) => {
                const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                if (svgRect) {
                  setSummaryChartTooltip({
                    key: macro.key,
                    x: Math.max(18, Math.min(svgRect.width - 18, event.clientX - svgRect.left)),
                    y: Math.max(18, Math.min(svgRect.height - 18, event.clientY - svgRect.top)),
                  });
                }
              }}
              onMouseLeave={() => {
                setSummaryChartHoverKey(null);
                setSummaryChartTooltip(null);
              }}
              onFocus={() => {
                setSummaryChartHoverKey(macro.key);
                setSummaryChartTooltip({
                  key: macro.key,
                  x: center,
                  y: center - innerRadius - 10,
                });
              }}
              onBlur={() => {
                setSummaryChartHoverKey(null);
                setSummaryChartTooltip(null);
              }}
              tabIndex={0}
              aria-label={`${macro.label} : ${formatSummaryLegendValue(macro.current, macro.unit)} ${macro.unit}`}
              style={{
                cursor: 'pointer',
                filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))'
              }}
            />
          </g>
        );
      }).filter(Boolean);

      return (
        <div className="visual-chart-container">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${size} ${size}`}
            style={{ maxWidth: 'min(100%, 340px)', margin: '0 auto', display: 'block' }}
          >
            {slices}
            <circle
              cx={center}
              cy={center}
              r={innerRadius}
              fill="white"
              stroke="#e1e8ed"
              strokeWidth="4"
              style={{
                filter: 'drop-shadow(0 8px 20px rgba(26, 46, 64, 0.10))'
              }}
            />
            <text
              x={center}
              y={center - 6}
              textAnchor="middle"
              fill="#1A2E40"
              fontSize="28"
              fontWeight="800"
              fontFamily="'Roboto', sans-serif"
            >
              {Math.round(Number.isFinite(directEnergyKcal) ? directEnergyKcal : 0)}
            </text>
            <text
              x={center}
              y={center + 16}
              textAnchor="middle"
              fill="#64748b"
              fontSize="13"
              fontWeight="700"
              fontFamily="'Roboto', sans-serif"
            >
              kcal
            </text>
            <text
              x={center}
              y={center + 34}
              textAnchor="middle"
              fill="#94a3b8"
              fontSize="11"
              fontWeight="600"
              fontFamily="'Roboto', sans-serif"
            >
              Macros sélectionnées
            </text>
          </svg>
          {activeTooltipMacro && (
            <div
              className="chart-hover-tooltip floating"
              role="status"
              aria-live="polite"
              style={{
                left: `${summaryChartTooltip?.x ?? 140}px`,
                top: `${summaryChartTooltip?.y ?? 140}px`,
              }}
            >
              <div className="chart-hover-tooltip-title">{activeTooltipMacro.label}</div>
              <div className="chart-hover-tooltip-meta">
                {`${formatSummaryLegendValue(activeTooltipMacro.current, activeTooltipMacro.unit)} ${activeTooltipMacro.unit}`}
                {Number.isFinite(activeTooltipMacro.goal) ? ` · objectif ${formatSummaryLegendValue(activeTooltipMacro.goal, activeTooltipMacro.unit)} ${activeTooltipMacro.unit}` : ''}
              </div>
              <div className="chart-hover-tooltip-share">
                {activeTooltipPercentage.toFixed(1)}% du graphique
              </div>
            </div>
          )}
          <div className="chart-legend">
            {summaryLegendData.map((macro) => {
              const percentage = Number(macro.percentage || 0);

              return (
                <div key={macro.key} className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ backgroundColor: macro.color }} />
                  <div className="chart-legend-main">
                    <span className="chart-legend-label">{macro.label}</span>
                    <span className="chart-legend-meta">
                      {`${formatSummaryLegendValue(macro.current, macro.unit)} ${macro.unit}`}
                      {Number.isFinite(macro.goal) ? ` · objectif ${formatSummaryLegendValue(macro.goal, macro.unit)} ${macro.unit}` : ''}
                    </span>
                  </div>
                  <span className="chart-legend-share">
                    {percentage >= 10 ? `${Math.round(percentage)}%` : `${percentage.toFixed(1)}%`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <SummaryWrapper>
        <SummaryTitle>Résumé de la journée</SummaryTitle>

        <div className="fusion-summary-content">
          <div className="chart-center-section">
            {renderPieChart()}
          </div>

          <div className="macros-grid">
            {effectiveSummaryMacros.map(macroKey => {
              const macroConfig = SUMMARY_MACRO_MAPPING[macroKey];

              if (!macroConfig) {
                return null;
              }

              const current = totals[macroConfig.dataKey] || 0;
              const displayLabel = String(macroConfig.label || '').replace(/^\w/u, (ch) => ch.toUpperCase());
              const rawGoal = macroConfig.goalKey ? nutritionalGoals[macroConfig.goalKey] : null;
              const parsedGoal = Number(rawGoal);
              const macroSupportsGoal =
                DEFAULT_GOAL_SUMMARY_MACROS.has(macroKey)
                || (advancedGoalsEnabled && ADVANCED_GOAL_SUMMARY_MACROS.has(macroKey));
              const hasGoal =
                macroSupportsGoal
                && Number.isFinite(parsedGoal)
                && parsedGoal > 0;
              const goal = hasGoal ? parsedGoal : null;
              const remaining = hasGoal ? goal - current : null;
              const percentage = hasGoal ? (current / goal) * 100 : 0;

              return (
                <div key={macroKey} className="macro-card-fusion">
                  <div className="macro-card-header">
                    <span className="macro-title">{displayLabel}</span>
                  </div>

                  <div className="macro-values-row">
                    <div className="value-block">
                      <div className="value-label">Total</div>
                      <div className="value-number" style={{ color: macroConfig.color }}>
                        {macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(current) : current.toFixed(1)}
                        <span className="value-unit">{macroConfig.unit}</span>
                      </div>
                    </div>

                    {hasGoal && (
                      <>
                        <div className="value-block">
                          <div className="value-label">Objectif</div>
                          <div className="value-number secondary">
                            {macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(goal) : goal.toFixed(0)}
                            <span className="value-unit">{macroConfig.unit}</span>
                          </div>
                        </div>

                        <div className="value-block">
                          <div className="value-label">Reste</div>
                          <div
                            className={`value-number ${remaining >= 0 ? 'positive' : 'negative'}`}
                          >
                            {remaining >= 0 ? '+' : ''}{macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(remaining) : remaining.toFixed(0)}
                            <span className="value-unit">{macroConfig.unit}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {hasGoal && (
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${Math.min(100, percentage)}%`,
                          backgroundColor: macroConfig.color
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </SummaryWrapper>
    );
  };

  if (loading && !isInitialized) {
    return (
      <div className="journal-container">
        <div className="loading-state">
          <LoadingSpinner type="bars" size="medium" color="primary" showText={false} />
        </div>
      </div>
    );
  }

  if (error && !isInitialized) {
    return (
      <div className="journal-container">
        <div className="error-state">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </div>
          <p>Erreur: {error}</p>
          <button onClick={clearError} className="btn btn-primary">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`journal-container ${isReadOnly ? 'friend-view' : ''}`}>
        {showModal && modalConfig && (
          <Modal
            title={modalConfig.title}
            message={modalConfig.message}
            onConfirm={modalConfig.onConfirm}
            onCancel={modalConfig.onCancel}
            confirmText={modalConfig.confirmText}
            cancelText={modalConfig.cancelText}
            singleButton={modalConfig.singleButton}
            confirmDisabled={modalConfig.confirmDisabled}
            confirmVariant={modalConfig.confirmVariant}
            size={modalConfig.size}
            className={modalConfig.className}
          >
            {modalConfig.children}
          </Modal>
        )}

        {isCopyDrawerOpen && (
          <div className="copy-center-overlay" onClick={closeCopyCenter}>
            <aside className="copy-center-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="copy-center-header">
                <div>
                  <h3>Copier des repas</h3>
                  <p>
                    <FontAwesomeIcon icon={faCalendarDays} />
                    Journal affiché: {formatDateShort(currentDateKey)}
                  </p>
                </div>
                <button
                  type="button"
                  className="copy-center-close-btn"
                  onClick={closeCopyCenter}
                  disabled={isProcessing}
                  aria-label="Fermer"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              <div className="copy-center-body">
                <div className="copy-center-mode-grid">
                  <button
                    type="button"
                    className={`copy-center-mode-btn ${copyMode === 'full_day_copy' ? 'active' : ''}`}
                    onClick={() => switchCopyMode('full_day_copy')}
                    disabled={isProcessing}
                  >
                    <span className="copy-center-mode-title">Copier une journée complète</span>
                    <span className="copy-center-mode-subtitle">Copie tous les repas d'une date source vers une date destination</span>
                  </button>
                  <button
                    type="button"
                    className={`copy-center-mode-btn ${copyMode === 'meal_copy' ? 'active' : ''}`}
                    onClick={() => switchCopyMode('meal_copy')}
                    disabled={isProcessing}
                  >
                    <span className="copy-center-mode-title">Copier un repas</span>
                    <span className="copy-center-mode-subtitle">Copie un repas depuis une date source vers une date destination</span>
                  </button>
                </div>
                <p className="copy-center-date-hint" id="copy-center-date-hint">
                  Choisissez quoi copier, puis ajustez seulement les champs nécessaires.
                </p>

                {copyMode === 'full_day_copy' && (
                  <div className="copy-center-form-grid">
                    <label className="copy-center-field">
                      <span>Date source</span>
                      <SharedDatePicker
                        value={copySourceDate}
                        onChange={setCopySourceDate}
                        maxDateKey={currentDateKey}
                        aria-label="Date source pour copier une journee complete"
                        dialogLabel="Sélecteur de date source"
                        disabled={isProcessing}
                      />
                    </label>

                    <label className="copy-center-field">
                      <span>Date destination</span>
                      <SharedDatePicker
                        value={copyTargetDate}
                        onChange={setCopyTargetDate}
                        aria-label="Date destination pour copier une journee complete"
                        dialogLabel="Sélecteur de date destination"
                        disabled={isProcessing}
                      />
                    </label>
                  </div>
                )}

                {copyMode === 'meal_copy' && (
                  <div className="copy-center-form-grid">
                    <label className="copy-center-field">
                      <span>Date source</span>
                      <SharedDatePicker
                        value={copySourceDate}
                        onChange={setCopySourceDate}
                        maxDateKey={currentDateKey}
                        aria-label="Date source pour copier un repas vers la date affichee"
                        dialogLabel="Sélecteur de date source"
                        disabled={isProcessing}
                      />
                    </label>

                    <label className="copy-center-field">
                      <span>Date destination</span>
                      <SharedDatePicker
                        value={copyTargetDate}
                        onChange={setCopyTargetDate}
                        aria-label="Date destination pour copier un repas"
                        dialogLabel="Sélecteur de date destination"
                        disabled={isProcessing}
                      />
                    </label>

                    <label className="copy-center-field">
                      <span>Repas source</span>
                      <select
                        value={copySourceMealId}
                        onChange={(e) => setCopySourceMealId(e.target.value)}
                        disabled={isProcessing || sourceMealsForSelectedDate.length === 0}
                      >
                        <option value="">Choisir un repas source</option>
                        {sourceMealsForSelectedDate.map((meal) => (
                          <option key={meal.id} value={String(meal.id)}>
                            {getMealDisplayLabel(meal)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="copy-center-field">
                      <span>Repas destination</span>
                      <select
                        value={copyDestinationMealId}
                        onChange={(e) => setCopyDestinationMealId(e.target.value)}
                        disabled={isProcessing || targetMealsForSelectedDate.length === 0}
                      >
                        <option value="">Choisir un repas destination</option>
                        {targetMealsForSelectedDate.map((meal) => (
                          <option key={meal.id} value={String(meal.id)}>
                            {getMealDisplayLabel(meal)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                <div className="copy-center-summary" aria-live="polite">
                  <span className="copy-center-summary-label">{copySummary.title}</span>
                  <div className="copy-center-summary-flow">
                    <span>{copySummary.source}</span>
                    <FontAwesomeIcon icon={faChevronDown} className="copy-center-summary-arrow" />
                    <span>{copySummary.destination}</span>
                  </div>
                  <p>{copySummary.detail}</p>
                </div>
              </div>

              <div className="copy-center-footer">
                <button
                  type="button"
                  className="copy-center-btn secondary"
                  onClick={closeCopyCenter}
                  disabled={isProcessing}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="copy-center-btn primary"
                  onClick={handleCopyCenterSubmit}
                  disabled={isProcessing}
                >
                  Lancer la copie
                </button>
              </div>
            </aside>
          </div>
        )}

        <SearchFoodForm
          visible={searchState.visible}
          onClose={closeSearch}
          onSelectProduct={(product) => selectProduct(product)}
          onEditProduct={handleEditProductFromSearch}
          hideUnavailableMacros={hideUnavailableFoodMacros}
          invalidatedBarcode={invalidatedBarcode}
          onAddNewFood={handleAddNewFoodFromSearch}
        />

        <Toast />

        <FoodImageModal
          isOpen={Boolean(foodImagePreview)}
          images={
            foodImagePreview
              ? getFoodImageGallery(foodImagePreview)
              : []
          }
          fallbackSrc={PRODUCT_PLACEHOLDER_IMAGE}
          title={foodImagePreview?.name || "Produit"}
          subtitle={foodImagePreview?.brand || ""}
          code={foodImagePreview?.code || ""}
          onClose={() => setFoodImagePreview(null)}
        />

        <div className={`journal-container ${isMobileViewport ? 'mobile-journal-mode' : ''}`}>
          <div className="day-header">
            <h2 className="day-header-title">Journal Alimentaire</h2>
            <div className="day-header-nav">
              <button type="button" className="day-nav-btn" onClick={() => changeDay(-1)} aria-label="Jour précédent">←</button>
              <SharedDatePicker
                value={currentDateKey}
                onChange={handleJournalDateChange}
              />
              <button type="button" className="day-nav-btn" onClick={() => changeDay(1)} aria-label="Jour suivant">→</button>
            </div>
            {!isReadOnly && isMobileViewport && (
              <button
                type="button"
                className={`day-header-actions-toggle ${isMobileHeaderActionsOpen ? 'active' : ''}`}
                onClick={() => setIsMobileHeaderActionsOpen((prev) => !prev)}
                aria-label={isMobileHeaderActionsOpen ? 'Masquer les actions du journal' : 'Afficher les actions du journal'}
                aria-expanded={isMobileHeaderActionsOpen}
              >
                Actions
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path
                    d="M6.5 9.5 12 15l5.5-5.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            {!isReadOnly && (
              <div className={`day-header-actions ${isMobileViewport ? (isMobileHeaderActionsOpen ? 'open' : 'collapsed') : ''}`}>
                <button
                  type="button"
                  className="day-header-action-btn"
                  onClick={() => {
                    if (isMobileViewport) {
                      setIsMobileHeaderActionsOpen(false);
                    }
                    openCopyCenter();
                  }}
                  disabled={isProcessing}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  Copier
                </button>
                <button
                  type="button"
                  className="day-header-action-btn"
                  onClick={() => {
                    if (isMobileViewport) {
                      setIsMobileHeaderActionsOpen(false);
                    }
                    handleExportRequest('pdf');
                  }}
                  disabled={!hasAnyExportableFoods}
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  Exporter PDF
                </button>
                <button
                  type="button"
                  className="day-header-action-btn"
                  onClick={() => {
                    if (isMobileViewport) {
                      setIsMobileHeaderActionsOpen(false);
                    }
                    handleExportRequest('excel');
                  }}
                  disabled={!hasAnyExportableFoods}
                >
                  <FontAwesomeIcon icon={faFileExcel} />
                  Exporter Excel
                </button>
              </div>
            )}
          </div>

          {renderMobileCalorieOverview()}

          <div id="meals" className="journal-meals-list">
            {filteredMeals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" width="32" height="32">
                    <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z" />
                  </svg>
                </div>
                <h3>Aucun repas pour {formatDateShort(currentDate)}</h3>
                <p>Ajoutez votre premier repas</p>
                {!isReadOnly && (
                  <button onClick={addMeal} className="btn btn-primary add-first-meal">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    Ajouter un repas
                  </button>
                )}
              </div>
            ) : (
              <>
                <div
                  className="drop-zone"
                  onDragOver={(e) => {
                    if (!isMobileViewport) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    if (!isMobileViewport) handleDrop(e, 0);
                  }}
                />
                {filteredMeals.map((meal, idx) => (
                  <React.Fragment key={meal.id}>
                    {renderMealElement(meal, idx)}
                    <div
                      className="drop-zone"
                      onDragOver={(e) => {
                        if (!isMobileViewport) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (!isMobileViewport) {
                          const targetIndex = Number.isInteger(draggedItem) && draggedItem > idx
                            ? idx + 1
                            : idx;
                          handleDrop(e, targetIndex);
                        }
                      }}
                    />
                  </React.Fragment>
                ))}
                {!isReadOnly && (
                  <button className="add-meal-btn" onClick={addMeal}>+ Ajouter un repas</button>
                )}
                {renderSummaryElement()}
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default Journal;
